import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert, Box, Typography, Stack, Chip, Grid, Button, Select, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  Tooltip, IconButton, Card, CardContent, useTheme, useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PrintIcon from '@mui/icons-material/Print';
import SyncIcon from '@mui/icons-material/Sync';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import facturaVentaService, { FacturaVenta, resolveMasterNumber } from '../../services/facturaVentaService';
import ubicacionService, { etiquetaUbicacionSeleccion } from '../../services/ubicacionService';
import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import clienteService, {
  OPTIONS_CLIENTE_OPERATIVO,
  esErrorClienteNoOperativo,
  getMensajeClienteNoOperativo,
} from '../../services/clienteService';
import productoService from '../../services/productoService';
import { calcularTotalesIvaVenta } from '../../utils/calcularTotalesIvaVenta';
import { transformarPedidoAFactura } from '../../utils/pedidoToFactura';
import { transformarCotizacionAFactura } from '../../utils/cotizacionToFactura';
import { resolverPrecioListaDetalle } from '../../utils/normalizarDetallesFacturaVenta';
import { advertirLineasConPrecioCero } from '../../utils/pedidoVentaLineas';
import { primerErrorPrecioMinimoLineas } from '../../utils/validarPrecioMinimoLinea';
import { extractLoteFlagsFromProducto, mensajeValidacionLotes } from '../../utils/loteLineaHelpers';
import { facturaVentaDescuentaStock } from '../../utils/politicaStockVenta';
import { parseNroUbicacionFromLabel } from '../../utils/facturaToNotaCreditoPos';
import { obtenerCodigoListaCliente, obtenerPrecioBaseProducto, resolverPrecioDesdeLista } from '../../utils/precioListaPedido';
import articuloXListaPrecioService from '../../services/articuloXListaPrecioService';
import {
  contextoDescuentoDesdeCliente,
  recalcularDescuentosVenta,
  resolverBonificacionesCliente,
  resolverPorcDescLinea,
} from '../../services/descuentoService';
import {
  resolverUmYPrecioDesdeDetalle,
} from '../../utils/umVentaDocumento';
import { fetchStockDisponibleEnUm } from '../../utils/stockUmVenta';
import { formatCurrency, normalizeItemNotas, parseLocalDate, toLocalDateStringYMD, truncToDecimals } from '../../utils/formatters';
import {
  esClienteSuspendido,
  mensajeConfirmacionClienteSuspendido,
  mensajeHardBlockClienteSuspendido,
  type BloqueoClienteSuspendidoModo,
} from '../../utils/clienteEstadoFacturacion';
import {
  calcularFechaVencimDesdeVtoComprob,
  validarFechasFacturaVenta,
} from '../../utils/facturaVentaFechas';
import { labelVtoComprob, resolveVtoComprobCliente } from '../../utils/vtoComprobCliente';
import ClienteInfo360Dialog from '../clientes/info360/ClienteInfo360Dialog';
import FacturaPOSClientePanel from './pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from './pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from './pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel from './pos/FacturaPOSTotalesPanel';
import FacturaPOSPagoPanel from './pos/FacturaPOSPagoPanel';
import FacturaPOSGrupoCajaDialog from './pos/FacturaPOSGrupoCajaDialog';
import FacturaPOSAtajosDialog from './pos/FacturaPOSAtajosDialog';
import { construirLineasDesdeUltimoPedido } from './pos/repetirUltimoPedido';
import {
  construirLineasDesdeFactura,
  mensajeOmitidasDiscontinuadas,
} from './pos/copiarDesdeFactura';
import FacturaPOSUltimasFacturasDialog from './pos/FacturaPOSUltimasFacturasDialog';
import { POSCliente, POSLinea } from './pos/types';
import ConfirmCancelHotkeyDialog from '../../components/ConfirmCancelHotkeyDialog';
import FormHotkeyBar, { type FormHotkeyBarItem } from '../../components/FormHotkeyBar';
import FacturarOrigenDialog, { FacturarOrigenTipo } from '../../components/ventas/FacturarOrigenDialog';
import { usePosConfigurableHotkeys, type PosHotkeyHandlerMap } from '../../hooks/usePosConfigurableHotkeys';
import { usePostEmisionEnvioFacturaEmail } from '../../hooks/usePostEmisionEnvioFacturaEmail';
import { useCan } from '../../hooks/useCan';
import {
  POS_HOTKEY_ACTIONS,
  POS_HOTKEY_POST_EMISION_IDS,
  formatPosHotkeyLabel,
  getPosHotkeyDefaultBindings,
  mergePosHotkeyBindings,
  type PosHotkeyAccionId,
  type PosHotkeyBindings,
} from '../../config/posFacturaHotkeys';
import facturaPosAtajosService from '../../services/facturaPosAtajosService';
import { resolverNombreCliente } from './pos/clienteNombre';
import {
  lineasBloqueanEdicionCliente,
  masterFieldsParaLinea,
  MSG_CLIENTE_BLOQUEADO_POR_MASTER,
} from './pos/lineaMasterCliente';
import { pendienteMetaDesdeMaster } from './pos/pendienteMetaLinea';
import {
  aplicarPorcentajeDescuento,
  esTipoIvaPreciosConIva,
  multiplicadorIvaProducto,
  round2,
  subtotalLineaConIvaDisplay,
} from './pos/preciosIvaDisplay';
import usePosCobro from './hooks/usePosCobro';
import { usePosTicketPrinter } from '../../hooks/usePosTicketPrinter';
import { useClientePredeterminadoSucursal } from '../../hooks/useClientePredeterminadoSucursal';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import {
  filtrarSucursalesPorCompania,
  resolveNroSucParaCompania,
  resolveCompaniaYSucursalInicial,
  persistCompaniaYSucursalInicial,
} from '../../utils/sucursalPreferida';
import {
  persistGlobalIdCompania,
} from '../../utils/companiaPreferida';
import { NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA } from '../../utils/posImpresionConfig';
import configuracionService from '../../services/configuracionService';
import type { ReciboCreateResponse } from '../../services/tesoreriaService';
import PDFViewer from '../../components/shared/PDFViewer';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import ventasSeguimientoService, {
  VentaPendienteMaster,
  VentaHiloComprobante,
  cantidadSugeridaDesdePendienteVenta,
  filtrarPendientesPorArticulo,
  filtrarPendientesPorEstado,
  filtrarPendientesPorFechaEmision,
  formatComprobanteVentaMasterDisplay,
  formatComprobanteVentaHiloLine,
  cantidadEntregadaPendiente,
  cantidadDevueltaPendiente,
} from '../../services/ventasSeguimientoService';
import { getPedidoEstadoChip, PEDIDO_ESTADO_PENDIENTES_FILTER_OPTIONS } from '../../utils/pedidoVentaEstados';
import { resolverEstadoPendientePedido } from '../../utils/enrichPendientesVentaEstado';
import type { AfipRescatePosPayload, FacturaPosLocationState } from '../../config/routes';
import { facturaPosEditarPath, facturaPosPath, facturaPosVerPath } from '../../config/routes';
import ordenReparacionService from '../../services/ordenReparacionService';
import { lineaPosDesdeItemOrdenTrabajo } from '../../utils/ordenTrabajoFactura';
import { formaDePagoDesdeCondVta } from '../../utils/formaPagoVenta';
import { FORMAS_DE_PAGO } from './pos/constants';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { FACTURAS_POS_TOUR } from '../../config/tours/facturasVentaTour';

const resolverFormaDePagoCliente = (cliente: unknown): string => {
  if (!cliente || typeof cliente !== 'object') return '';
  const o = cliente as Record<string, unknown>;
  const raw = String(o.condVta ?? o.condvta ?? o.CondVta ?? '').trim();
  return formaDePagoDesdeCondVta(FORMAS_DE_PAGO, raw, '');
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const notasDeDetallePedido = (d: PedidoVentaDetalle): string => {
  const raw = d as PedidoVentaDetalle & { Notas?: string | null };
  return normalizeItemNotas(raw.notas ?? raw.Notas);
};

const codigoDetallePedido = (d: PedidoVentaDetalle): string => {
  const raw = d as PedidoVentaDetalle & { CodigoArticulo?: string | null };
  return String(raw.codigoArticulo ?? raw.CodigoArticulo ?? '').trim();
};

const nroItemDetallePedido = (d: PedidoVentaDetalle): number => {
  const raw = d as PedidoVentaDetalle & { NroItem?: number | null };
  return Number(raw.nroItem ?? raw.NroItem ?? 0) || 0;
};

const descripcionDetallePedido = (d: PedidoVentaDetalle): string => {
  const raw = d as PedidoVentaDetalle & {
    DescripcionArticulo?: string | null;
    descripcion?: string | null;
    Descripcion?: string | null;
  };
  return String(
    raw.descripcionArticulo ?? raw.DescripcionArticulo ?? raw.descripcion ?? raw.Descripcion ?? '',
  ).trim();
};

const normalizarTextoMatch = (s?: string | null): string =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

type VentaPendienteEnriquecido = VentaPendienteMaster & {
  /** Detalle del pedido origen (si se pudo resolver al enriquecer). */
  detallePedido?: PedidoVentaDetalle;
};

/** Busca el detalle del pedido que corresponde al pendiente. */
const findDetalleEnPedido = (
  pedido: PedidoVenta,
  pendiente: VentaPendienteMaster,
): PedidoVentaDetalle | undefined => {
  const detalles = pedido.detalles ?? [];
  const itemMaster = pendiente.nroItemMaster;
  const codigo = (pendiente.codigoArticulo ?? '').trim();
  const descPend = normalizarTextoMatch(pendiente.descripcionArticulo);

  if (codigo && descPend) {
    const porCodigoYDesc = detalles.find(
      (d) =>
        codigoDetallePedido(d) === codigo &&
        normalizarTextoMatch(descripcionDetallePedido(d)) === descPend,
    );
    if (porCodigoYDesc) return porCodigoYDesc;
  }

  const porNroItem = detalles.find((d) => nroItemDetallePedido(d) === itemMaster);
  if (porNroItem) return porNroItem;

  const porNroItemMaster = detalles.find(
    (d) =>
      resolveMasterNumber(d as unknown as Record<string, unknown>, [
        'nroItemMaster',
        'NroItemMaster',
        'nroitemmaster',
      ]) === itemMaster,
  );
  if (porNroItemMaster) return porNroItemMaster;

  if (codigo) {
    const porCodigo = detalles.filter((d) => codigoDetallePedido(d) === codigo);
    if (porCodigo.length > 0) {
      const porItemEnCodigo = porCodigo.find((d) => nroItemDetallePedido(d) === itemMaster);
      if (porItemEnCodigo) return porItemEnCodigo;

      if (descPend) {
        const porDescParcial = porCodigo.find((d) => {
          const descDet = normalizarTextoMatch(descripcionDetallePedido(d));
          return descDet === descPend || descDet.startsWith(descPend) || descPend.startsWith(descDet);
        });
        if (porDescParcial) return porDescParcial;
      }

      if (porCodigo.length === 1) return porCodigo[0];
    }
  }

  return undefined;
};

/** Notas + detalle desde el pedido origen (fuente de verdad; el seguimiento puede estar desactualizado). */
const enrichPendientesConNotasPedido = async (
  rows: VentaPendienteMaster[],
): Promise<VentaPendienteEnriquecido[]> => {
  if (rows.length === 0) return rows;

  const formularios = Array.from(new Set(rows.map((p) => p.nroFormularioMaster).filter((n) => n > 0)));
  const pedidosPorFormulario = new Map<number, PedidoVenta>();

  await Promise.all(
    formularios.map(async (nroFormulario) => {
      try {
        const pedido = await pedidoVentaService.getPedidoVenta(nroFormulario, { sinCache: true });
        pedidosPorFormulario.set(nroFormulario, pedido);
      } catch {
        /* pedido no disponible: se conservan notas del endpoint de pendientes */
      }
    }),
  );

  return rows.map((p) => {
    const pedido = pedidosPorFormulario.get(p.nroFormularioMaster);
    const estadoMaster = resolverEstadoPendientePedido(p, pedido);
    const descripcionPedido = String(pedido?.descripcion ?? '').trim() || undefined;

    if (!pedido) {
      const notasEndpoint = String(p.notas ?? '').trim() || undefined;
      // Si el endpoint metió la descripción de cabecera en notas, no duplicarla.
      const notasLimpias =
        notasEndpoint && descripcionPedido && notasEndpoint === descripcionPedido
          ? undefined
          : notasEndpoint;
      return {
        ...p,
        estadoMaster,
        descripcionPedido: descripcionPedido ?? p.descripcionPedido,
        notas: notasLimpias,
      };
    }

    const detallePedido = findDetalleEnPedido(pedido, p);
    const notasPedido = detallePedido ? notasDeDetallePedido(detallePedido).trim() : undefined;
    const notasLimpias =
      notasPedido && descripcionPedido && notasPedido === descripcionPedido
        ? undefined
        : notasPedido || undefined;

    if (!detallePedido) {
      return { ...p, notas: undefined, estadoMaster, descripcionPedido };
    }

    return {
      ...p,
      detallePedido,
      notas: notasLimpias,
      estadoMaster,
      descripcionPedido,
    };
  });
};

/** Número de vendedor y nombre desde el maestro de clientes (incl. campo legacy `vendedor` y PascalCase). */
const vendedorDesdePayloadCliente = (src: unknown): { nroVendedor: number; nombreVendedor?: string } => {
  if (!src || typeof src !== 'object') return { nroVendedor: 0 };
  const o = src as Record<string, unknown>;
  const vi = o.vendedorInfo ?? o.VendedorInfo;
  const viObj = vi && typeof vi === 'object' ? (vi as Record<string, unknown>) : null;
  const nFromNested = Number(viObj?.nroVendedor ?? viObj?.NroVendedor ?? 0) || 0;
  const nFromRoot =
    Number(o.vendedor ?? o.Vendedor ?? o.nroVendedor ?? o.NroVendedor ?? 0) || 0;
  const nroVendedor = nFromNested > 0 ? nFromNested : nFromRoot;
  const nombreNested = viObj
    ? String(viObj.nombreVendedor ?? viObj.NombreVendedor ?? '').trim()
    : '';
  return {
    nroVendedor: nroVendedor > 0 ? nroVendedor : 0,
    nombreVendedor: nombreNested || undefined,
  };
};

const mergeClientePosDesdeApi = (src: unknown): POSCliente => {
  const c = src as POSCliente;
  const o = src && typeof src === 'object' ? (src as Record<string, unknown>) : {};
  const nm = resolverNombreCliente(src);
  const nro = Number(c.nroCliente) || 0;
  const nombreFinal = nm || String(c.nombre ?? '').trim() || `Cliente #${nro}`;
  const razonFinal = nm || String(c.razonSocial ?? '').trim() || nombreFinal;
  const { nroVendedor, nombreVendedor } = vendedorDesdePayloadCliente(src);
  const dirEnv = String(c.direccionEnvio ?? o.DireccionEnvio ?? '').trim();
  const codDirEnv = String(c.codigoDireccionEnvio ?? o.CodigoDireccionEnvio ?? '').trim();
  const codCiudadEnv = Number(c.codigoCiudadEnvio ?? o.CodigoCiudadEnvio);
  const cli: POSCliente = {
    ...c,
    nombre: nombreFinal,
    razonSocial: razonFinal,
    direccionEnvio: dirEnv || undefined,
    codigoDireccionEnvio: codDirEnv || undefined,
    codigoCiudadEnvio:
      Number.isFinite(codCiudadEnv) && codCiudadEnv > 0 ? codCiudadEnv : c.codigoCiudadEnvio,
  };
  if (nroVendedor > 0) {
    cli.nroVendedor = nroVendedor;
    cli.vendedorInfo = {
      nroVendedor,
      nombreVendedor: nombreVendedor || cli.vendedorInfo?.nombreVendedor || undefined,
    };
  }
  const vtoComprob = resolveVtoComprobCliente(src);
  if (vtoComprob != null) {
    cli.vtoComprob = vtoComprob;
  }
  const tipoCliente = String(c.tipoCliente ?? o.TipoCliente ?? '').trim();
  if (tipoCliente) cli.tipoCliente = tipoCliente;
  const notasRaw = c.notas ?? o.Notas;
  if (notasRaw != null && String(notasRaw).trim() !== '') {
    cli.notas = String(notasRaw).trim();
  }
  return cli;
};

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = round2((Number(l.cantidad) || 0) * (Number(l.precioUnitario) || 0));
  return aplicarPorcentajeDescuento(base, l.descuento);
};

/** Lee UM del detalle tolerando camelCase / PascalCase. */
const umDesdeDetalleDoc = (d: unknown): string => {
  const o = (d ?? {}) as Record<string, unknown>;
  return String(o.um ?? o.Um ?? o.UM ?? '').trim();
};

/** Mapea un detalle de pedido a línea POS (mismo criterio que pedidoventa/lineasDesdeDetalles). */
const lineaPosDesdeDetallePedido = (
  d: PedidoVentaDetalle,
  nroCliente: number,
): POSLinea => {
  const cantidad = Number(d.cantidad ?? 0);
  const precioUnitario = Number(d.precioUnitario ?? 0);
  const porcDescRaw = Number(d.porcDesc ?? d.descuento ?? 0);
  const porcDesc =
    porcDescRaw > 0 && porcDescRaw <= 1
      ? Math.max(0, Math.min(100, porcDescRaw * 100))
      : Math.max(0, Math.min(100, porcDescRaw));
  const precioUnitarioFinal = Number(d.precioUnitarioFinal ?? 0);
  const subtotalFinal = Number(d.subtotalFinal ?? 0);
  const nfm = Number(d.nroFormularioMaster);
  const nim = Number(d.nroItemMaster);
  const nroForm = Number(d.nroIdformulario);
  const nroItem = Number(d.nroItem);
  const masterForm =
    Number.isFinite(nfm) && nfm > 0
      ? nfm
      : Number.isFinite(nroForm) && nroForm > 0
        ? nroForm
        : NaN;
  const masterItem =
    Number.isFinite(nim) && nim > 0
      ? nim
      : Number.isFinite(nroItem) && nroItem > 0
        ? nroItem
        : NaN;

  const linea: POSLinea = {
    id: genId(),
    codigoArticulo: String(d.codigoArticulo ?? '').trim(),
    descripcionArticulo: String(d.descripcionArticulo ?? '').trim(),
    cantidad,
    precioUnitario,
    descuento: porcDesc,
    descuentoManual: true,
    subtotal: Number(d.subtotal ?? 0) || calcularSubtotal({
      cantidad,
      precioUnitario,
      descuento: porcDesc,
    }),
    um: umDesdeDetalleDoc(d),
    notas: d.notas,
    ...(precioUnitarioFinal > 0 ? { precioUnitarioFinal } : {}),
    ...(subtotalFinal > 0 ? { subtotalFinal } : {}),
    ...(Number.isFinite(masterForm) && masterForm > 0 && Number.isFinite(masterItem) && masterItem > 0
      ? masterFieldsParaLinea(masterForm, masterItem, nroCliente)
      : {}),
  };
  return linea;
};

/** Cantidad a facturar desde un pendiente (prioriza pendienteFacturar). */
const cantidadDesdePendiente = (p: VentaPendienteMaster): number => {
  const cantidadPendienteFacturar = Number(p.pendienteFacturar) || 0;
  return cantidadPendienteFacturar > 0
    ? cantidadPendienteFacturar
    : cantidadSugeridaDesdePendienteVenta(p);
};

/**
 * Mapea un pendiente a línea POS.
 * Si hay detalle de pedido enriquecido, reutiliza precios/finales/um como al facturar el pedido.
 */
const lineaPosDesdePendiente = (
  p: VentaPendienteEnriquecido,
  nroCliente: number,
): POSLinea => {
  const cantidad = cantidadDesdePendiente(p);
  const nroCli = Number(p.nroCliente ?? nroCliente) || 0;
  const detalle = p.detallePedido;

  if (detalle) {
    const base = lineaPosDesdeDetallePedido(detalle, nroCli);
    const cantidadPedido = Number(detalle.cantidad ?? 0) || 0;
    const linea: POSLinea = {
      ...base,
      id: genId(),
      cantidad,
      notas: normalizeItemNotas(p.notas) || base.notas,
      ...masterFieldsParaLinea(p.nroFormularioMaster, p.nroItemMaster, nroCli),
      pendienteMeta: pendienteMetaDesdeMaster(p),
    };
    linea.subtotal = calcularSubtotal(linea);

    const subtotalFinalOrig = Number(detalle.subtotalFinal ?? 0);
    if (subtotalFinalOrig > 0 && cantidadPedido > 0) {
      linea.subtotalFinal = round2(subtotalFinalOrig * (cantidad / cantidadPedido));
    } else if (linea.precioUnitarioFinal && linea.precioUnitarioFinal > 0) {
      linea.subtotalFinal = round2(linea.precioUnitarioFinal * cantidad);
    }

    return linea;
  }

  const precioLista = Number(p.precioListaMaster ?? p.precioUnitarioMaster ?? 0) || 0;
  const porcDesc = Number(p.porcDesc1Master ?? 0) || 0;
  const linea: POSLinea = {
    id: genId(),
    codigoArticulo: p.codigoArticulo || '',
    descripcionArticulo: p.descripcionArticulo || '',
    cantidad,
    precioUnitario: precioLista,
    descuento: porcDesc,
    descuentoManual: true,
    subtotal: 0,
    um: '',
    notas: normalizeItemNotas(p.notas),
    ...masterFieldsParaLinea(p.nroFormularioMaster, p.nroItemMaster, nroCli),
    pendienteMeta: pendienteMetaDesdeMaster(p),
  };
  linea.subtotal = calcularSubtotal(linea);
  return linea;
};

/**
 * Al traer un pendiente/pedido a factura se conserva el precio, la cantidad y la UM
 * del documento origen (sin reconvertir a UM Ref. Factura / PAQ).
 * Si la UM no vino en el pedido, se completa con lógica de pedido
 * (umrefPed → equivalencias → umrefFac), sin tocar el precio.
 */
const ajustarLineaPendienteAUmFactura = async (linea: POSLinea): Promise<POSLinea> => {
  if (String(linea.um ?? '').trim()) return linea;
  const cod = String(linea.codigoArticulo ?? '').trim();
  if (!cod) return linea;
  try {
    const detalle = await productoService.getEquivalenciasUmDetalle(cod);
    const r = resolverUmYPrecioDesdeDetalle(
      { codigoArticulo: cod },
      Number(linea.precioUnitario) || 0,
      'pedido',
      detalle,
    );
    if (r.um) return { ...linea, um: r.um };
  } catch {
    /* conservar línea sin UM */
  }
  return linea;
};

interface Sucursal { nroSuc: number; nombreSucursal?: string; idCompania?: string | null; }
interface Compania { idCompania: string; nombre: string; condIva?: string; }

const extractApiError = (e: unknown): string => {
  const data = (e as { response?: { data?: { caeError?: unknown } } })?.response?.data;
  if (data && typeof data === 'object' && data.caeError) {
    return String(data.caeError);
  }
  return getApiErrorMessage(e, 'Error al guardar la factura');
};

const padComprobanteNum = (value: unknown, len: number) => {
  const n = Number(value);
  if (Number.isFinite(n)) return String(n).padStart(len, '0');
  return String(value ?? '').padStart(len, '0');
};

const formatComprobanteEmitido = (f: {
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
  nroFormulario?: number;
} | null | undefined): string => {
  if (!f) return '';
  const letra = String(f.letra ?? '').trim().toUpperCase();
  const ns = f.nroSuc;
  const nc = f.nroComprobante;
  if (letra && ns != null && nc != null) {
    return `${letra}-${padComprobanteNum(ns, 5)}-${padComprobanteNum(nc, 8)}`;
  }
  if (f.nroFormulario) return `#${f.nroFormulario}`;
  return '';
};

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

function afipFechaToYmd(fecha?: string | null): string | null {
  const f = (fecha ?? '').trim();
  if (!f) return null;
  if (/^\d{8}$/.test(f)) return `${f.slice(0, 4)}-${f.slice(4, 6)}-${f.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(f)) return f.slice(0, 10);
  return null;
}

function parseArcaImporte(v?: string | number | null): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const NuevaFacturaPOS: React.FC = () => {
  useRegisterPageTour(FACTURAS_POS_TOUR);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const puedeEditarFacturaPos =
    superAdmin ||
    Boolean(user?.roles?.includes('BACKDOOR')) ||
    Boolean(user?.roles?.includes('SOPORTE-IT'));

  const ventaPendienteInicial = (location.state as FacturaPosLocationState | null)?.ventaPendiente;
  const afipRescate: AfipRescatePosPayload | null =
    (location.state as FacturaPosLocationState | null)?.afipRescate ?? null;

  const nroFormularioParam = searchParams.get('nroFormulario');
  const nroVer = nroFormularioParam ? parseInt(nroFormularioParam, 10) : NaN;
  const modoVerPos = Number.isFinite(nroVer) && nroVer > 0;
  const modoRescate = Boolean(afipRescate) && !modoVerPos;
  const modoEditarPos = modoVerPos && searchParams.get('editar') === '1';
  const modoVerSoloLectura = modoVerPos && !modoEditarPos;

  const pedidoIdParam = searchParams.get('pedidoId');
  const pedidoIdNum = pedidoIdParam ? parseInt(pedidoIdParam, 10) : NaN;
  const ordenTrabajoIdParam = searchParams.get('ordenTrabajoId');
  const ordenTrabajoIdNum = ordenTrabajoIdParam ? parseInt(ordenTrabajoIdParam, 10) : NaN;
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const cotizacionIdNum = cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : NaN;
  const facturaIdParam = searchParams.get('facturaId');
  const facturaIdNum = facturaIdParam ? parseInt(facturaIdParam, 10) : NaN;

  const modoDesdePedido = Number.isFinite(pedidoIdNum) && pedidoIdNum > 0 && !modoVerPos;
  const modoDesdeOrdenTrabajo =
    Number.isFinite(ordenTrabajoIdNum) && ordenTrabajoIdNum > 0 && !modoVerPos && !modoDesdePedido;
  const modoDesdeCotizacion =
    Number.isFinite(cotizacionIdNum) && cotizacionIdNum > 0 && !modoVerPos && !modoDesdePedido && !modoDesdeOrdenTrabajo;
  const modoDesdeFacturaOrigen =
    Number.isFinite(facturaIdNum) &&
    facturaIdNum > 0 &&
    !modoVerPos &&
    !modoDesdePedido &&
    !modoDesdeOrdenTrabajo &&
    !modoDesdeCotizacion;
  const modoDesdePendienteInicial =
    Boolean(ventaPendienteInicial) &&
    !modoVerPos &&
    !modoDesdePedido &&
    !modoDesdeOrdenTrabajo &&
    !modoDesdeCotizacion &&
    !modoDesdeFacturaOrigen;

  const [loadingFactura, setLoadingFactura] = useState(modoVerPos);
  const [loadingPedido, setLoadingPedido] = useState(
    modoDesdePedido || modoDesdeOrdenTrabajo || modoDesdeCotizacion || modoDesdeFacturaOrigen,
  );
  const [pendingApplied, setPendingApplied] = useState(false);
  const [tituloPos, setTituloPos] = useState('');
  const [nroPedidoOrigen, setNroPedidoOrigen] = useState<number | null>(null);
  const [nroOrdenTrabajoOrigen, setNroOrdenTrabajoOrigen] = useState<number | null>(null);
  const [nroCotizacionOrigen, setNroCotizacionOrigen] = useState<number | null>(null);
  const [facturaVerPos, setFacturaVerPos] = useState<{
    nroFormulario: number;
    nroAsiento: number | null;
    importeTotal: number;
    importeSinIva: number;
    ivainscripto: number;
    tipoIva?: string;
    letra?: string;
  } | null>(null);
  const [pedidoOrigenTotales, setPedidoOrigenTotales] = useState<{
    importeTotal: number;
    importeSinIva: number;
    ivainscripto: number;
    tipoIva?: string;
  } | null>(null);
  const [generandoAsiento, setGenerandoAsiento] = useState(false);
  const [openGenerarAsientoDialog, setOpenGenerarAsientoDialog] = useState(false);
  const [horaEmisionFactura, setHoraEmisionFactura] = useState<Date | null>(null);

  const {
    sucursales: sucursalesUsuario,
    preferidaNro,
    preferidaSucursal,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();
  const sucursales: Sucursal[] = sucursalesUsuario;
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [idCompania, setIdCompania] = useState<string>('');
  const [loadingSuc, setLoadingSuc] = useState(false);
  const loadingSucCombined = loadingSuc || loadingSucursalesUsuario;
  const initCompaniaSucursalRef = useRef(false);

  useEffect(() => {
    const cargar = async () => {
      setLoadingSuc(true);
      try {
        const resCom = await api.get('/api/Compañias');
        const comRaw = Array.isArray(resCom.data) ? resCom.data : resCom.data?.items ?? [];
        const comData: Compania[] = comRaw.map((c: Record<string, unknown>) => ({
          idCompania: String(c.idCompania ?? c.IdCompania ?? '').trim(),
          nombre: String(c.nombre ?? c.Nombre ?? '').trim(),
          condIva: c.condIva != null || c.CondIva != null
            ? String(c.condIva ?? c.CondIva).trim()
            : undefined,
        })).filter((c: Compania) => Boolean(c.idCompania));

        setCompanias(comData);
      } catch {
        /* compañías no disponibles */
      } finally {
        setLoadingSuc(false);
      }
    };
    cargar();
  }, []);

  // Preferida (estrella) + su compañía al abrir; al cambiar compañía se recalcula sucursal
  const sucursalUsuarioEditadaRef = useRef(false);

  const sucursalesDeCompania = useMemo(
    () => filtrarSucursalesPorCompania(sucursales, idCompania),
    [sucursales, idCompania],
  );

  const handleIdCompaniaChange = useCallback(
    (id: string) => {
      if (modoRescate) return;
      const next = String(id ?? '').trim();
      setIdCompania(next);
      if (next) persistGlobalIdCompania(next);
      if (
        modoVerPos ||
        modoDesdePedido ||
        modoDesdeOrdenTrabajo ||
        modoDesdeCotizacion ||
        modoDesdeFacturaOrigen
      ) {
        return;
      }
      sucursalUsuarioEditadaRef.current = false;
      const nro = resolveNroSucParaCompania({
        idCompania: next,
        sucursales,
        preferidaNro,
        storedNroSuc: Number(localStorage.getItem('nroSuc') || '0') || null,
        keepCurrentIfValid: false,
      });
      if (nro != null && nro > 0) {
        setNroSuc(nro);
        persistGlobalNroSuc(nro);
      } else {
        setNroSuc(0);
      }
    },
    [
      sucursales,
      preferidaNro,
      modoRescate,
      modoVerPos,
      modoDesdePedido,
      modoDesdeOrdenTrabajo,
      modoDesdeCotizacion,
      modoDesdeFacturaOrigen,
    ],
  );

  useEffect(() => {
    if (
      modoRescate ||
      modoVerPos ||
      modoDesdePedido ||
      modoDesdeOrdenTrabajo ||
      modoDesdeCotizacion ||
      modoDesdeFacturaOrigen
    ) {
      return;
    }
    sucursalUsuarioEditadaRef.current = false;
    initCompaniaSucursalRef.current = false;
    setNroSuc(0);
    setIdCompania('');
  }, [
    location.key,
    modoRescate,
    modoVerPos,
    modoDesdePedido,
    modoDesdeOrdenTrabajo,
    modoDesdeCotizacion,
    modoDesdeFacturaOrigen,
  ]);

  useEffect(() => {
    if (
      modoRescate ||
      modoVerPos ||
      modoDesdePedido ||
      modoDesdeOrdenTrabajo ||
      modoDesdeCotizacion ||
      modoDesdeFacturaOrigen
    ) {
      return;
    }
    if (loadingSucursalesUsuario || loadingSuc) return;
    if (sucursales.length === 0 || companias.length === 0) return;
    if (sucursalUsuarioEditadaRef.current) return;
    if (initCompaniaSucursalRef.current) return;

    const inicial = resolveCompaniaYSucursalInicial({
      sucursales,
      preferidaNro,
      preferidaSucursal,
      companias,
    });
    if (!inicial) return;
    initCompaniaSucursalRef.current = true;
    setIdCompania(inicial.idCompania);
    setNroSuc(inicial.nroSuc);
    persistCompaniaYSucursalInicial(inicial);
  }, [
    loadingSucursalesUsuario,
    loadingSuc,
    sucursales,
    preferidaNro,
    preferidaSucursal,
    companias,
    modoRescate,
    modoVerPos,
    modoDesdePedido,
    modoDesdeOrdenTrabajo,
    modoDesdeCotizacion,
    modoDesdeFacturaOrigen,
    location.key,
  ]);

  // Solo persistir la preferida si pertenece a la compañía actual
  useEffect(() => {
    const nro = resolveNroSucParaCompania({
      idCompania,
      sucursales,
      preferidaNro,
      keepCurrentIfValid: false,
    });
    if (nro != null && preferidaNro != null && Number(preferidaNro) === nro) {
      persistGlobalNroSuc(nro);
    }
  }, [preferidaNro, sucursales, idCompania]);

  useEffect(() => {
    if (modoVerPos || modoDesdePedido || modoDesdeOrdenTrabajo || modoDesdeCotizacion || modoDesdeFacturaOrigen) return;
    setCliente(null);
    setLineas([]);
    setPorcDesc(0);
    setPorcDesc2(0);
    setPorcDesc3(0);
    setFormaDePago('');
    setOrdenCompra('');
    setTransporte('');
    setUbicacion('');
    setFacturaEmitida(null);
    setTituloPos('');
    setNroPedidoOrigen(null);
    setNroCotizacionOrigen(null);
    setFechaEmision(toLocalDateStringYMD());
    setFechaVencim(toLocalDateStringYMD());
    setObservaciones('');
  }, [modoVerPos, modoDesdePedido, modoDesdeOrdenTrabajo, modoDesdeCotizacion, modoDesdeFacturaOrigen]);

  /** Si no hay ubicación (factura POS nueva), usar la primera del catálogo. */
  useEffect(() => {
    if (modoVerPos || modoDesdePedido || modoDesdeOrdenTrabajo || modoDesdeCotizacion || modoDesdeFacturaOrigen) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await ubicacionService.getUbicaciones();
        if (cancelled || !list?.length) return;
        setUbicacion((prev) => {
          if (prev && prev.trim()) return prev;
          const u = list[0];
          return etiquetaUbicacionSeleccion(u);
        });
      } catch {
        /* sin catálogo */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoVerPos, modoDesdePedido, modoDesdeOrdenTrabajo, modoDesdeCotizacion, modoDesdeFacturaOrigen]);

  useEffect(() => {
    if (!modoRescate || !afipRescate) return;
    setIdCompania(afipRescate.idCompania);
    setNroSuc(afipRescate.puntoVenta);
    persistGlobalIdCompania(afipRescate.idCompania);
    persistGlobalNroSuc(afipRescate.puntoVenta);
    sucursalUsuarioEditadaRef.current = true;
    initCompaniaSucursalRef.current = true;
    setFormaDePago('CTA.CTE.');
    const ymd = afipFechaToYmd(afipRescate.fechaComprobante);
    if (ymd) {
      setFechaEmision(ymd);
      setFechaVencim(ymd);
    }
    const letra = (afipRescate.letra ?? '').trim().toUpperCase();
    const pv = String(afipRescate.puntoVenta).padStart(4, '0');
    const nro = String(afipRescate.numeroComprobante).padStart(8, '0');
    setTituloPos(letra ? `${letra}-${pv}-${nro}` : `PV ${pv}-${nro}`);
  }, [modoRescate, afipRescate, location.key]);

  useEffect(() => {
    if (!modoVerPos) return;
    let cancelled = false;
    (async () => {
      setLoadingFactura(true);
      try {
        const f: any = await facturaVentaService.getFacturaVenta(nroVer);
        if (cancelled) return;

        let ubicacionStr = f.ubicacion ?? '';
        if (!ubicacionStr && f.nroUbicacionOrigen) {
          try {
            const u = await ubicacionService.getUbicacion(f.nroUbicacionOrigen);
            if (u) ubicacionStr = etiquetaUbicacionSeleccion(u);
            else ubicacionStr = `(${f.nroUbicacionOrigen})`;
          } catch {
            ubicacionStr = `(${f.nroUbicacionOrigen})`;
          }
        }

        const letra = (f.letra ?? '').toString().trim().toUpperCase();
        const ns = f.nroSuc;
        const nc = f.nroComprobante;
        const padNum = (value: unknown, len: number) => {
          const n = Number(value);
          if (Number.isFinite(n)) return String(n).padStart(len, '0');
          return String(value ?? '').padStart(len, '0');
        };
        if (letra && ns != null && Number(nc) > 0) {
          setTituloPos(`${letra}-${padNum(ns, 5)}-${padNum(nc, 8)}`);
        } else {
          setTituloPos('');
        }

        let tipoIvaResuelto = String(f.tipoIva ?? '').trim();
        let clienteEnriquecido: POSCliente | null = null;
        if (f.nroCliente) {
          try {
            const c = await clienteService.getCliente(Number(f.nroCliente));
            if (!cancelled) {
              if (!tipoIvaResuelto) {
                tipoIvaResuelto = String(c.tipoIva ?? '').trim();
              }
              clienteEnriquecido = mergeClientePosDesdeApi(c);
            }
          } catch {
            /* sin cliente */
          }
        }
        if (cancelled) return;

        setFacturaVerPos({
          nroFormulario: Number(f.nroFormulario ?? nroVer),
          nroAsiento: f.nroAsiento != null ? Number(f.nroAsiento) : null,
          importeTotal: Number(f.importeTotal ?? 0),
          importeSinIva: Number(f.importeSinIva ?? 0),
          ivainscripto: Number(f.ivainscripto ?? 0),
          tipoIva: tipoIvaResuelto || undefined,
          letra: letra || undefined,
        });

        setNroSuc(f.nroSuc ?? 0);
        if (f.idCompania) setIdCompania(String(f.idCompania));
        setPorcDesc(Number(f.porcDesc ?? 0));
        setPorcDesc2(Number(f.porcDesc2 ?? 0));
        setPorcDesc3(Number(f.porcDesc3 ?? 0));
        setFormaDePago((f.formaDePago ?? '').trim());
        setOrdenCompra((f.ordenDeCompra ?? f.ordenCompra ?? '').trim());
        setTransporte((f.formaEntrega ?? f.transporte ?? '').trim());
        setUbicacion(ubicacionStr);
        if (f.fechaEmision) {
          const d = new Date(f.fechaEmision);
          const ymdEm = !Number.isNaN(d.getTime()) ? toLocalDateStringYMD(d) : toLocalDateStringYMD();
          setFechaEmision(ymdEm);
          if (f.horaEmision) {
            const h = new Date(f.horaEmision);
            setHoraEmisionFactura(!Number.isNaN(h.getTime()) ? h : null);
          } else {
            setHoraEmisionFactura(null);
          }
          const vto = clienteEnriquecido?.vtoComprob ?? resolveVtoComprobCliente(f) ?? null;
          const fvRaw = f.fechaVencim ?? f.FechaVencim;
          if (fvRaw) {
            const dv = new Date(fvRaw);
            setFechaVencim(
              !Number.isNaN(dv.getTime())
                ? toLocalDateStringYMD(dv)
                : toLocalDateStringYMD(calcularFechaVencimDesdeVtoComprob(parseLocalDate(ymdEm), vto)),
            );
          } else {
            setFechaVencim(
              toLocalDateStringYMD(calcularFechaVencimDesdeVtoComprob(parseLocalDate(ymdEm), vto)),
            );
          }
        } else {
          const hoy = toLocalDateStringYMD();
          setFechaEmision(hoy);
          setFechaVencim(hoy);
        }
        setObservaciones(String(f.descripcion ?? '').trim());

        const nmFactura = resolverNombreCliente(f) || String(f.nombre ?? '').trim() || `Cliente #${f.nroCliente ?? 0}`;
        setCliente({
          nroCliente: f.nroCliente ?? 0,
          nombre: nmFactura,
          razonSocial: nmFactura,
          cuit: f.cuit ?? undefined,
          tipoIva: tipoIvaResuelto || undefined,
          condVta: f.formaDePago ?? undefined,
          nroLista: f.nroLista ?? undefined,
          nroVendedor: f.nroVendedor ?? undefined,
          vtoComprob: clienteEnriquecido?.vtoComprob ?? resolveVtoComprobCliente(f) ?? undefined,
          direccionEnvio: String(f.direccionEnvio ?? f.DireccionEnvio ?? '').trim() || undefined,
          codigoDireccionEnvio: String(f.codigoDireccionEnvio ?? f.CodigoDireccionEnvio ?? '').trim() || undefined,
          codigoCiudadEnvio: (() => {
            const raw = f.codigoCiudadEnvio ?? f.CodigoCiudadEnvio;
            const n = Number(raw);
            return Number.isFinite(n) && n > 0 ? n : undefined;
          })(),
          vendedorInfo:
            f.usuario || f.nroVendedor != null
              ? { nroVendedor: Number(f.nroVendedor ?? 0), nombreVendedor: f.usuario ?? undefined }
              : undefined,
        });

        const detalles = f.detalles ?? [];
        const codigos: string[] = Array.from(
          new Set(
            detalles
              .map((d: { codigoArticulo?: string }) => String(d.codigoArticulo ?? '').trim())
              .filter((c: string) => c.length > 0),
          ),
        );
        const fetchedProducts: Record<string, unknown> = {};
        await Promise.all(
          codigos.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              if (producto) fetchedProducts[codigo] = producto;
            } catch {
              /* sin producto */
            }
          }),
        );
        if (cancelled) return;
        if (Object.keys(fetchedProducts).length > 0) {
          setProductosIvaCache((prev) => ({ ...prev, ...fetchedProducts }));
        }

        setLineas(
          detalles.map((d: any) => {
            const cantidad = Number(d.cantidad ?? 0);
            const precioUnitario = Number(d.precioUnitario ?? d.precioLista ?? 0);
            const precioUnitarioFinal = Number(d.precioUnitarioFinal ?? 0);
            const subtotalFinal = Number(d.subtotalFinal ?? 0);
            const descuento = Number(d.porcDesc ?? d.descuento ?? 0);
            const nfm = Number(d.nroFormularioMaster);
            const nim = Number(d.nroItemMaster);
            const codigo = String(d.codigoArticulo ?? '').trim();
            const loteFlags = extractLoteFlagsFromProducto(fetchedProducts[codigo]);
            const lotesRaw = Array.isArray(d.lotes) ? d.lotes : Array.isArray(d.Lotes) ? d.Lotes : [];
            const lotes = lotesRaw
              .map((l: any) => ({
                idLote: l.idLote ?? l.IdLote ?? null,
                nroLote: String(l.nroLote ?? l.NroLote ?? '').trim() || null,
                fechaVencimiento: l.fechaVencimiento ?? l.FechaVencimiento ?? null,
                fechaFabricacion: l.fechaFabricacion ?? l.FechaFabricacion ?? null,
                cantidad: Number(l.cantidad ?? l.Cantidad ?? 0),
                observaciones: l.observaciones ?? l.Observaciones ?? null,
              }))
              .filter((l: { cantidad: number }) => Number.isFinite(l.cantidad) && l.cantidad > 0);
            const linea: POSLinea = {
              id: genId(),
              codigoArticulo: codigo,
              descripcionArticulo: (d.descripcionArticulo ?? '').trim(),
              cantidad,
              precioUnitario,
              descuento,
              subtotal: 0,
              um: (d.um ?? '').trim(),
              notas: d.notas,
              controlLoteSerie: loteFlags.controlLoteSerie,
              requiereVencimiento: loteFlags.requiereVencimiento,
              ...(lotes.length > 0 ? { lotes } : {}),
              ...(precioUnitarioFinal > 0 ? { precioUnitarioFinal } : {}),
              ...(subtotalFinal > 0 ? { subtotalFinal } : {}),
              ...(Number.isFinite(nfm) && nfm > 0 && Number.isFinite(nim) && nim > 0
                ? masterFieldsParaLinea(nfm, nim, Number(f.nroCliente) || 0)
                : {}),
            };
            linea.subtotal = calcularSubtotal(linea);
            return linea;
          }),
        );
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar la factura', { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
          navigate('/ventas/facturas', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingFactura(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoVerPos, nroVer, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!modoDesdePedido) return;
    let cancelled = false;
    (async () => {
      setLoadingPedido(true);
      try {
        const pedido = await pedidoVentaService.getPedidoVenta(pedidoIdNum);
        const factura = await transformarPedidoAFactura(pedido);
        if (cancelled) return;

        setNroPedidoOrigen(pedidoIdNum);
        const ns = Number(factura.nroSuc ?? 0);
        if (ns > 0) setNroSuc(ns);
        if (factura.idCompania) setIdCompania(String(factura.idCompania));
        setPorcDesc(Number(factura.porcDesc ?? 0));
        setPorcDesc2(Number(factura.porcDesc2 ?? 0));
        setPorcDesc3(Number(factura.porcDesc3 ?? 0));
        const fpRaw = (factura.formaDePago ?? '').trim();
        if (fpRaw) {
          const fp = fpRaw.replace(' ', '.').toUpperCase();
          setFormaDePago(['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'].includes(fp) ? fp : fpRaw);
        }
        setOrdenCompra((factura as any).ordenCompra ?? (factura as any).ordenDeCompra ?? '');
        setTransporte((factura.transporte ?? '').trim());
        let ub = (factura.ubicacion ?? '').trim();
        if (!ub) {
          try {
            const lu = await ubicacionService.getUbicaciones();
            if (lu?.length) {
              const u0 = lu[0];
              ub = etiquetaUbicacionSeleccion(u0);
            }
          } catch {
            /* sin catálogo */
          }
        }
        setUbicacion(ub);
        const fe = (factura as any).fechaEmision;
        let ymdEmisionPedido = toLocalDateStringYMD();
        if (fe) {
          const d = new Date(fe);
          ymdEmisionPedido = !Number.isNaN(d.getTime()) ? toLocalDateStringYMD(d) : toLocalDateStringYMD();
        }
        setFechaEmision(ymdEmisionPedido);
        setObservaciones(String((factura as any).descripcion ?? '').trim());

        const nroCli = Number(factura.nroCliente ?? pedido.nroCliente ?? 0);
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
          throw new Error('El pedido no tiene un cliente válido');
        }

        let tipoIvaResuelto = String(
          (pedido as any).tipoIva ?? (pedido as any).tipoIVA ?? (factura as any).tipoIVA ?? '',
        ).trim();

        try {
          const c = await clienteService.getCliente(nroCli);
          if (!tipoIvaResuelto) {
            tipoIvaResuelto = String(c.tipoIva ?? '').trim();
          }
          const ce = c as any;
          const nmCli = resolverNombreCliente(c) || String(c.nombre ?? '').trim() || `Cliente #${c.nroCliente}`;
          const mergedCli = mergeClientePosDesdeApi(c);
          setCliente({
            ...mergedCli,
            nroCliente: c.nroCliente,
            nombre: nmCli,
            razonSocial: nmCli,
            cuit: c.cuit || factura.cuit,
            dni: ce.dni,
            tipoIva: c.tipoIva || tipoIvaResuelto || undefined,
            condVta: c.condVta || factura.formaDePago,
            nroLista: c.nroLista || factura.nroLista,
            nroVendedor: factura.nroVendedor || (Number(ce.vendedor) > 0 ? Number(ce.vendedor) : undefined),
            nroExpreso: factura.transporte ? Number(factura.transporte) : (ce.nroExpreso ?? undefined),
            porcDesc: c.porcDesc ?? factura.porcDesc ?? undefined,
            porcDesc2: c.porcDesc2 ?? factura.porcDesc2 ?? undefined,
            porcDesc3: c.porcDesc3 ?? factura.porcDesc3 ?? undefined,
            listaPrecio: ce.listaPrecio,
            direccionEnvio:
              String(c.direccionEnvio ?? (factura as any).direccionEnvio ?? '').trim() || undefined,
            codigoDireccionEnvio: String(c.codigoDireccionEnvio ?? '').trim() || undefined,
            vendedorInfo:
              pedido.usuario || factura.usuario
                ? { nroVendedor: Number(factura.nroVendedor ?? 0), nombreVendedor: pedido.usuario || factura.usuario }
                : undefined,
          });
          setFechaVencim(
            toLocalDateStringYMD(
              calcularFechaVencimDesdeVtoComprob(
                parseLocalDate(ymdEmisionPedido),
                mergedCli.vtoComprob ?? null,
              ),
            ),
          );
        } catch {
          const nmPedido = resolverNombreCliente(factura) || String(factura.nombre ?? '').trim() || `Cliente #${nroCli}`;
          setCliente({
            nroCliente: nroCli,
            nombre: nmPedido,
            razonSocial: nmPedido,
            cuit: factura.cuit,
            tipoIva: tipoIvaResuelto || undefined,
            condVta: factura.formaDePago,
            nroLista: factura.nroLista,
            nroVendedor: factura.nroVendedor,
            nroExpreso: factura.transporte ? Number(factura.transporte) : undefined,
            porcDesc: factura.porcDesc ?? undefined,
            porcDesc2: factura.porcDesc2 ?? undefined,
            porcDesc3: factura.porcDesc3 ?? undefined,
            direccionEnvio: String((factura as any).direccionEnvio ?? '').trim() || undefined,
            vendedorInfo:
              pedido.usuario || factura.usuario
                ? { nroVendedor: Number(factura.nroVendedor ?? 0), nombreVendedor: pedido.usuario || factura.usuario }
                : undefined,
          });
        }

        const detallesPedido = pedido.detalles ?? [];
        const codigos: string[] = Array.from(
          new Set(
            detallesPedido
              .map((d) => String(d.codigoArticulo ?? '').trim())
              .filter((c) => c.length > 0),
          ),
        );
        const fetchedProducts: Record<string, unknown> = {};
        await Promise.all(
          codigos.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              if (producto) fetchedProducts[codigo] = producto;
            } catch {
              /* sin producto */
            }
          }),
        );
        if (cancelled) return;
        if (Object.keys(fetchedProducts).length > 0) {
          setProductosIvaCache((prev) => ({ ...prev, ...fetchedProducts }));
        }

        setPedidoOrigenTotales({
          importeTotal: Number(pedido.importeTotal ?? factura.importeTotal ?? 0),
          importeSinIva: Number((pedido as any).importeSinIva ?? 0),
          ivainscripto: Number((pedido as any).ivainscripto ?? 0),
          tipoIva: tipoIvaResuelto || undefined,
        });

        setLineas(
          await Promise.all(
            detallesPedido.map((d) =>
              ajustarLineaPendienteAUmFactura(lineaPosDesdeDetallePedido(d, nroCli)),
            ),
          ),
        );
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar el pedido', { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
          navigate('/ventas/pedidos', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoDesdePedido, pedidoIdNum, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!modoDesdeOrdenTrabajo) return;
    let cancelled = false;
    (async () => {
      setLoadingPedido(true);
      try {
        const orden = await ordenReparacionService.getOrden(ordenTrabajoIdNum);
        if (cancelled) return;

        setNroOrdenTrabajoOrigen(ordenTrabajoIdNum);
        const nroCli = Number(orden.nroCliente ?? 0);
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
          throw new Error('La orden de trabajo no tiene un cliente válido');
        }

        const ymdEmision = String(orden.fechaEmision ?? '').slice(0, 10) || toLocalDateStringYMD();
        setFechaEmision(ymdEmision);
        setObservaciones(String(orden.detalleReparacion ?? '').trim());
        if (orden.nroTransporte != null && String(orden.nroTransporte).trim() !== '') {
          setTransporte(String(orden.nroTransporte).trim());
        }

        try {
          const c = await clienteService.getCliente(nroCli);
          if (cancelled) return;
          const merged = mergeClientePosDesdeApi(c);
          const nmCli =
            resolverNombreCliente(c) ||
            String(orden.nombreCliente ?? '').trim() ||
            String(merged.nombre ?? '').trim() ||
            `Cliente #${nroCli}`;
          setCliente({
            ...merged,
            nroCliente: c.nroCliente,
            nombre: nmCli,
            razonSocial: nmCli,
            cuit: c.cuit,
            tipoIva: c.tipoIva || undefined,
            condVta: c.condVta,
            nroLista: c.nroLista,
            nroVendedor:
              orden.nroVendedor ??
              (Number((c as { vendedor?: number }).vendedor) > 0
                ? Number((c as { vendedor?: number }).vendedor)
                : undefined),
            direccionEnvio: String(orden.direccionCliente ?? c.direccion ?? '').trim() || undefined,
          });
          setFechaVencim(
            toLocalDateStringYMD(
              calcularFechaVencimDesdeVtoComprob(parseLocalDate(ymdEmision), merged.vtoComprob ?? null),
            ),
          );
          const fp = resolverFormaDePagoCliente(c);
          if (fp) setFormaDePago(fp);
        } catch {
          const nm = String(orden.nombreCliente ?? '').trim() || `Cliente #${nroCli}`;
          setCliente({
            nroCliente: nroCli,
            nombre: nm,
            razonSocial: nm,
            nroVendedor: orden.nroVendedor ?? undefined,
            direccionEnvio: String(orden.direccionCliente ?? '').trim() || undefined,
          });
        }

        const items = orden.items ?? [];
        const codigos = Array.from(
          new Set(items.map((it) => String(it.codigoArticulo ?? '').trim()).filter((c) => c.length > 0)),
        );
        const preciosProducto: Record<string, number> = {};
        await Promise.all(
          codigos.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              const precio = Number((producto as { precioVenta?: number }).precioVenta ?? 0) || 0;
              if (precio > 0) preciosProducto[codigo] = precio;
            } catch {
              /* sin maestro */
            }
          }),
        );
        if (cancelled) return;

        const itemsConPrecio = items.map((it) => {
          const cod = String(it.codigoArticulo ?? '').trim();
          const precioLista = Number(it.precioLista ?? it.precioUnitario ?? preciosProducto[cod] ?? 0) || 0;
          return {
            ...it,
            precioLista: it.precioLista ?? (precioLista > 0 ? precioLista : undefined),
            precioUnitario: it.precioUnitario ?? (precioLista > 0 ? precioLista : undefined),
          };
        });

        if (Object.keys(preciosProducto).length > 0) {
          setProductosIvaCache((prev) => {
            const next = { ...prev };
            for (const codigo of codigos) {
              if (preciosProducto[codigo] != null && !next[codigo]) {
                next[codigo] = { codigoArticulo: codigo, precioVenta: preciosProducto[codigo] };
              }
            }
            return next;
          });
        }

        setLineas(itemsConPrecio.map((it, idx) => lineaPosDesdeItemOrdenTrabajo(orden, it, idx, nroCli)));
      } catch (e: unknown) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar la orden de trabajo', {
            variant: 'error',
            style: { whiteSpace: 'pre-wrap' },
          });
          navigate('/servicio/ordenes-reparacion', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoDesdeOrdenTrabajo, ordenTrabajoIdNum, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!modoDesdeCotizacion) return;
    let cancelled = false;
    (async () => {
      setLoadingPedido(true);
      try {
        const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionIdNum);
        const factura = await transformarCotizacionAFactura(cotizacion);
        if (cancelled) return;

        setNroCotizacionOrigen(cotizacionIdNum);
        const ns = Number(factura.nroSuc ?? 0);
        if (ns > 0) setNroSuc(ns);
        if (factura.idCompania) setIdCompania(String(factura.idCompania));
        setPorcDesc(Number(factura.porcDesc ?? 0));
        setPorcDesc2(Number(factura.porcDesc2 ?? 0));
        setPorcDesc3(Number(factura.porcDesc3 ?? 0));
        const fpRaw = (factura.formaDePago ?? '').trim();
        if (fpRaw) {
          const fp = fpRaw.replace(' ', '.').toUpperCase();
          setFormaDePago(['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'].includes(fp) ? fp : fpRaw);
        }
        const cotizacionExt = cotizacion as any;
        setOrdenCompra(
          String(cotizacionExt.ordenDeCompra ?? cotizacionExt.ordenCompra ?? (factura as any).ordenCompra ?? '').trim(),
        );
        setTransporte((factura.transporte ?? '').trim());
        let ub = (factura.ubicacion ?? '').trim();
        if (!ub) {
          try {
            const lu = await ubicacionService.getUbicaciones();
            if (lu?.length) ub = etiquetaUbicacionSeleccion(lu[0]);
          } catch {
            /* sin catálogo */
          }
        }
        setUbicacion(ub);
        const fe = (factura as any).fechaEmision;
        let ymdEmision = toLocalDateStringYMD();
        if (fe) {
          const d = new Date(fe);
          ymdEmision = !Number.isNaN(d.getTime()) ? toLocalDateStringYMD(d) : toLocalDateStringYMD();
        }
        setFechaEmision(ymdEmision);
        setObservaciones(String((factura as any).descripcion ?? '').trim());

        const nroCli = Number(factura.nroCliente ?? cotizacion.nroCliente ?? 0);
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
          throw new Error('La cotización no tiene un cliente válido');
        }

        let tipoIvaResuelto = String(
          cotizacionExt.tipoIva ?? cotizacionExt.tipoIVA ?? (factura as any).tipoIVA ?? '',
        ).trim();

        try {
          const c = await clienteService.getCliente(nroCli);
          if (!tipoIvaResuelto) tipoIvaResuelto = String(c.tipoIva ?? '').trim();
          const mergedCli = mergeClientePosDesdeApi(c);
          const nmCli = resolverNombreCliente(c) || String(c.nombre ?? '').trim() || `Cliente #${c.nroCliente}`;
          setCliente({
            ...mergedCli,
            nroCliente: c.nroCliente,
            nombre: nmCli,
            razonSocial: nmCli,
            cuit: c.cuit || factura.cuit,
            tipoIva: c.tipoIva || tipoIvaResuelto || undefined,
            condVta: c.condVta || factura.formaDePago,
            nroLista: c.nroLista || factura.nroLista,
            nroVendedor: factura.nroVendedor,
            porcDesc: c.porcDesc ?? factura.porcDesc ?? undefined,
            porcDesc2: c.porcDesc2 ?? factura.porcDesc2 ?? undefined,
            porcDesc3: c.porcDesc3 ?? factura.porcDesc3 ?? undefined,
          });
          setFechaVencim(
            toLocalDateStringYMD(
              calcularFechaVencimDesdeVtoComprob(parseLocalDate(ymdEmision), mergedCli.vtoComprob ?? null),
            ),
          );
        } catch {
          const nm = resolverNombreCliente(factura) || String(factura.nombre ?? '').trim() || `Cliente #${nroCli}`;
          setCliente({
            nroCliente: nroCli,
            nombre: nm,
            razonSocial: nm,
            cuit: factura.cuit,
            tipoIva: tipoIvaResuelto || undefined,
            condVta: factura.formaDePago,
            nroLista: factura.nroLista,
            nroVendedor: factura.nroVendedor,
            porcDesc: factura.porcDesc ?? undefined,
            porcDesc2: factura.porcDesc2 ?? undefined,
            porcDesc3: factura.porcDesc3 ?? undefined,
          });
        }

        const detallesCotizacion = cotizacion.detalles ?? [];
        setLineas(detallesCotizacion.map((d) => lineaPosDesdeDetallePedido(d as PedidoVentaDetalle, nroCli)));
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar la cotización', {
            variant: 'error',
            style: { whiteSpace: 'pre-wrap' },
          });
          navigate('/ventas/cotizaciones', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoDesdeCotizacion, cotizacionIdNum, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!modoDesdeFacturaOrigen) return;
    let cancelled = false;
    (async () => {
      setLoadingPedido(true);
      try {
        const data = await facturaVentaService.getFacturaVenta(facturaIdNum);
        if (cancelled) return;
        const f = data as any;

        let ubicacionStr = String(f.ubicacion ?? '').trim();
        if (!ubicacionStr && f.nroUbicacionOrigen) {
          try {
            const u = await ubicacionService.getUbicacion(Number(f.nroUbicacionOrigen));
            if (u) ubicacionStr = etiquetaUbicacionSeleccion(u);
            else ubicacionStr = `(${f.nroUbicacionOrigen})`;
          } catch {
            ubicacionStr = `(${f.nroUbicacionOrigen})`;
          }
        }

        const ns = Number(f.nroSuc ?? 0);
        if (ns > 0) setNroSuc(ns);
        if (f.idCompania) setIdCompania(String(f.idCompania));
        setPorcDesc(Number(f.porcDesc ?? 0));
        setPorcDesc2(Number(f.porcDesc2 ?? 0));
        setPorcDesc3(Number(f.porcDesc3 ?? 0));
        const fpRaw = String(f.formaDePago ?? '').trim();
        if (fpRaw) {
          const fp = fpRaw.replace(' ', '.').toUpperCase();
          setFormaDePago(['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'].includes(fp) ? fp : fpRaw);
        }
        setOrdenCompra(String(f.ordenDeCompra ?? f.ordenCompra ?? '').trim());
        setTransporte(String(f.formaEntrega ?? f.transporte ?? '').trim());
        setUbicacion(ubicacionStr);
        setFechaEmision(toLocalDateStringYMD());
        setObservaciones(String(f.descripcion ?? '').trim());

        const nroCli = Number(f.nroCliente ?? 0);
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
          throw new Error('La factura origen no tiene un cliente válido');
        }

        const tipoIvaResuelto = String(f.tipoIva ?? f.tipoIVA ?? '').trim();
        try {
          const c = await clienteService.getCliente(nroCli);
          const mergedCli = mergeClientePosDesdeApi(c);
          const nmCli = resolverNombreCliente(c) || String(c.nombre ?? '').trim() || `Cliente #${c.nroCliente}`;
          setCliente({
            ...mergedCli,
            nroCliente: c.nroCliente,
            nombre: nmCli,
            razonSocial: nmCli,
            cuit: c.cuit || String(f.cuit ?? ''),
            dni: String(f.dni ?? c.dni ?? ''),
            tipoIva: c.tipoIva || tipoIvaResuelto || undefined,
            condVta: c.condVta || String(f.formaDePago ?? ''),
            nroLista: c.nroLista || String(f.nroLista ?? ''),
            nroVendedor: Number(f.nroVendedor ?? 0) || undefined,
          });
          setFechaVencim(
            toLocalDateStringYMD(
              calcularFechaVencimDesdeVtoComprob(parseLocalDate(toLocalDateStringYMD()), mergedCli.vtoComprob ?? null),
            ),
          );
        } catch {
          const nm = String(f.nombre ?? '').trim() || `Cliente #${nroCli}`;
          setCliente({
            nroCliente: nroCli,
            nombre: nm,
            razonSocial: nm,
            cuit: String(f.cuit ?? ''),
            dni: String(f.dni ?? ''),
            tipoIva: tipoIvaResuelto || undefined,
            condVta: String(f.formaDePago ?? ''),
            nroLista: String(f.nroLista ?? ''),
            nroVendedor: Number(f.nroVendedor ?? 0) || undefined,
          });
        }

        const lineasFactura: POSLinea[] = (data.detalles || []).map((detalle) => {
          const precioLista = resolverPrecioListaDetalle(detalle);
          let porcDesc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
          if (porcDesc > 0 && porcDesc <= 1) porcDesc *= 100;
          porcDesc = Math.max(0, Math.min(100, porcDesc));
          const linea: POSLinea = {
            id: genId(),
            codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
            descripcionArticulo: String(detalle.descripcionArticulo ?? '').trim(),
            cantidad: Number(detalle.cantidad ?? 0),
            precioUnitario: precioLista,
            descuento: porcDesc,
            descuentoManual: true,
            subtotal: 0,
            um: String(detalle.um ?? '').trim(),
            notas: normalizeItemNotas(detalle.notas),
          };
          linea.subtotal = Number(detalle.subtotal ?? calcularSubtotal(linea));
          return linea;
        });
        setLineas(lineasFactura);
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar la factura origen', {
            variant: 'error',
            style: { whiteSpace: 'pre-wrap' },
          });
          navigate('/ventas/facturas', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoDesdeFacturaOrigen, facturaIdNum, enqueueSnackbar, navigate]);

  // ── Estado de la factura ─────────────────────────────────────
  const [cliente, setCliente] = useState<POSCliente | null>(null);
  const [lineas, setLineas] = useState<POSLinea[]>([]);
  const [productosIvaCache, setProductosIvaCache] = useState<Record<string, unknown>>({});
  const productosIvaCacheRef = useRef(productosIvaCache);
  productosIvaCacheRef.current = productosIvaCache;
  const [porcDesc, setPorcDesc] = useState(0);
  const [porcDesc2, setPorcDesc2] = useState(0);
  const [porcDesc3, setPorcDesc3] = useState(0);
  const [tipoDesc, setTipoDesc] = useState<'P' | 'M'>('P');
  const [tipoDesc2, setTipoDesc2] = useState<'P' | 'M'>('P');
  const [tipoDesc3, setTipoDesc3] = useState<'P' | 'M'>('P');
  const [importeDesc, setImporteDesc] = useState(0);
  const [importeDesc2, setImporteDesc2] = useState(0);
  const [importeDesc3, setImporteDesc3] = useState(0);
  const [formaDePago, setFormaDePago] = useState('');
  const [contadoPagoObligatorio, setContadoPagoObligatorio] = useState(true);
  const [bloqueoClienteSuspendido, setBloqueoClienteSuspendido] = useState<BloqueoClienteSuspendidoModo>('alert');
  const [ordenCompra, setOrdenCompra] = useState('');
  const [transporte, setTransporte] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  /** Si TipoFormulario factura (1) descuenta stock: se piden/validan lotes. */
  const [facturaDescuentaStock, setFacturaDescuentaStock] = useState(true);
  /** YYYY-MM-DD para el input de fecha de emisión */
  const [fechaEmision, setFechaEmision] = useState(() => toLocalDateStringYMD());
  /** YYYY-MM-DD; sugerida por Vto. Comprob., editable */
  const [fechaVencim, setFechaVencim] = useState(() => toLocalDateStringYMD());
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingRepetirUltimoPedido, setLoadingRepetirUltimoPedido] = useState(false);
  const [ultimasFacturasOpen, setUltimasFacturasOpen] = useState(false);
  const [info360Cliente, setInfo360Cliente] = useState<number | null>(null);
  const [caeErrorDialog, setCaeErrorDialog] = useState<{
    mensaje: string;
    caeObs?: string;
    payload: Record<string, unknown>;
  } | null>(null);
  const [facturaEmitida, setFacturaEmitida] = useState<any>(null);
  const [reciboEmitido, setReciboEmitido] = useState<ReciboCreateResponse | null>(null);
  const [savingPhase, setSavingPhase] = useState<'idle' | 'factura' | 'recibo'>('idle');
  const [cobroPendienteDialog, setCobroPendienteDialog] = useState<{
    factura: any;
    comprobanteLabel: string;
    mensaje: string;
  } | null>(null);
  const [cobroIncompletoAlEmitir, setCobroIncompletoAlEmitir] = useState(false);
  const [pdfReciboUrl, setPdfReciboUrl] = useState<string | null>(null);
  const [pdfReciboId, setPdfReciboId] = useState<number | null>(null);
  const [pdfReciboFileName, setPdfReciboFileName] = useState<string | null>(null);
  const posTicketPrinter = usePosTicketPrinter(nroSuc, {
    nroTipoFormulario: NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
    ubicacionEtiqueta: ubicacion,
    idCompania,
  });
  const clientePredeterminado = useClientePredeterminadoSucursal(nroSuc);
  const [clientePredApplyTick, setClientePredApplyTick] = useState(0);
  const ultimoClientePredAplicadoRef = useRef('');
  const clienteBonifSeqRef = useRef(0);

  const [openPendientes, setOpenPendientes] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<VentaPendienteEnriquecido[]>([]);
  const [pendientesFechaDesde, setPendientesFechaDesde] = useState('');
  const [pendientesFechaHasta, setPendientesFechaHasta] = useState('');
  const [pendientesBusquedaObra, setPendientesBusquedaObra] = useState('');
  const [pendientesFiltroEstado, setPendientesFiltroEstado] = useState('');
  const [pendientesPage, setPendientesPage] = useState(0);
  const [pendientesRowsPerPage, setPendientesRowsPerPage] = useState(10);
  const [agregandoTodos, setAgregandoTodos] = useState(false);
  /** Evita doble clic / carreras: un 2º "Agregar todos" duplica líneas. */
  const agregandoTodosRef = useRef(false);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [pendienteHiloItems, setPendienteHiloItems] = useState<VentaHiloComprobante[]>([]);
  const [loadingPendienteHilo, setLoadingPendienteHilo] = useState(false);

  const clienteEdicionBloqueada = useMemo(
    () => lineasBloqueanEdicionCliente(lineas, cliente?.nroCliente ?? 0),
    [lineas, cliente?.nroCliente],
  );

  const calcularVencimYmd = useCallback(
    (ymdEmision: string, vto?: number | null) =>
      toLocalDateStringYMD(
        calcularFechaVencimDesdeVtoComprob(parseLocalDate(ymdEmision.trim()), vto ?? null),
      ),
    [],
  );

  const handleFechaEmisionChange = useCallback((ymd: string) => {
    setFechaEmision(ymd);
    setFechaVencim((prevVencim) => {
      const prevCalc = calcularVencimYmd(fechaEmision, cliente?.vtoComprob);
      const newCalc = calcularVencimYmd(ymd, cliente?.vtoComprob);
      if (prevVencim === prevCalc) return newCalc;
      return prevVencim >= newCalc ? prevVencim : newCalc;
    });
  }, [fechaEmision, cliente?.vtoComprob, calcularVencimYmd]);

  const fechaVencimMin = useMemo(
    () => calcularVencimYmd(fechaEmision, cliente?.vtoComprob ?? null),
    [fechaEmision, cliente?.vtoComprob, calcularVencimYmd],
  );

  const handleFechaVencimChange = useCallback((ymd: string) => {
    if (ymd < fechaVencimMin) return;
    setFechaVencim(ymd);
  }, [fechaVencimMin]);

  const handleClienteSelect = useCallback((c: any) => {
    const cli = mergeClientePosDesdeApi(c);
    const nroNuevo = Number(cli.nroCliente) || 0;
    if (lineasBloqueanEdicionCliente(lineas, nroNuevo)) {
      enqueueSnackbar(MSG_CLIENTE_BLOQUEADO_POR_MASTER, { variant: 'warning' });
      return;
    }
    setCliente(cli);
    setFechaVencim(calcularVencimYmd(fechaEmision, cli.vtoComprob ?? null));
    if (!modoRescate) {
      const formaPagoInicial = resolverFormaDePagoCliente(cli);
      if (formaPagoInicial) setFormaDePago(formaPagoInicial);
    } else {
      setFormaDePago('CTA.CTE.');
    }
    if (cli.nroExpreso) setTransporte(String(cli.nroExpreso));

    const nroCliente = Number(cli.nroCliente) || 0;
    const seq = ++clienteBonifSeqRef.current;
    const aplicarBonif = (src: unknown) => {
      void resolverBonificacionesCliente(src, {
        codigoLista: obtenerCodigoListaCliente(src),
        nroVendedor: Number((src as { nroVendedor?: number; vendedor?: number }).nroVendedor
          ?? (src as { vendedor?: number }).vendedor) || null,
      }).then((desc) => {
        if (seq !== clienteBonifSeqRef.current) return;
        setPorcDesc(desc.porcDesc);
        setPorcDesc2(desc.porcDesc2);
        setPorcDesc3(desc.porcDesc3);
      });
    };
    aplicarBonif(cli);

    if (!nroCliente) return;
    void clienteService
      .getCliente(nroCliente)
      .then((full) => {
        if (!full || typeof full !== 'object') return;
        setCliente((prev) => {
          if (!prev || prev.nroCliente !== nroCliente) return prev;
          aplicarBonif(full);
          const nombreEditado = String(prev.nombre ?? '').trim();
          const merged = mergeClientePosDesdeApi({ ...prev, ...full });
          const vtoAnterior = prev.vtoComprob ?? null;
          const vtoNuevo = merged.vtoComprob ?? null;
          setFechaVencim((prevVencim) => {
            const calcAnterior = calcularVencimYmd(fechaEmision, vtoAnterior);
            const calcNuevo = calcularVencimYmd(fechaEmision, vtoNuevo);
            return prevVencim === calcAnterior ? calcNuevo : prevVencim;
          });
          if (!modoRescate) {
            const formaPagoCliente = resolverFormaDePagoCliente(merged);
            if (formaPagoCliente) setFormaDePago(formaPagoCliente);
          } else {
            setFormaDePago('CTA.CTE.');
          }
          // No pisar el nombre ingresado en «Datos del cliente» al completar el maestro
          if (nombreEditado && nombreEditado !== String(merged.nombre ?? '').trim()) {
            return { ...merged, nombre: nombreEditado, razonSocial: nombreEditado };
          }
          return merged;
        });
      })
      .catch(() => {});
  }, [lineas, enqueueSnackbar, fechaEmision, calcularVencimYmd, modoRescate]);

  const confirmarSiClienteSuspendido = useCallback((): boolean => {
    if (!cliente || !esClienteSuspendido(cliente.tipoCliente)) return true;
    if (bloqueoClienteSuspendido === 'hard') {
      enqueueSnackbar(
        mensajeHardBlockClienteSuspendido(cliente.nroCliente, cliente.nombre),
        { variant: 'error' },
      );
      return false;
    }
    if (bloqueoClienteSuspendido === 'alert') return true;
    return window.confirm(
      mensajeConfirmacionClienteSuspendido(cliente.nroCliente, cliente.nombre),
    );
  }, [cliente, bloqueoClienteSuspendido, enqueueSnackbar]);

  const guardadoBloqueadoPorClienteSuspendido = useMemo(
    () =>
      bloqueoClienteSuspendido === 'hard'
      && !!cliente
      && esClienteSuspendido(cliente.tipoCliente),
    [bloqueoClienteSuspendido, cliente],
  );

  const esFacturaPosNueva =
    !modoVerPos && !modoDesdePedido && !modoDesdeCotizacion && !modoDesdeFacturaOrigen;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const descuenta = await facturaVentaDescuentaStock();
      if (!cancelled) setFacturaDescuentaStock(descuenta);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!esFacturaPosNueva) return;
    if (clientePredeterminado.loading) return;
    const nroPred = clientePredeterminado.nroCliente;
    if (!clientePredeterminado.configurado || !nroPred || nroPred <= 0) return;

    const clave = `${nroSuc}:${nroPred}:${clientePredApplyTick}`;
    if (ultimoClientePredAplicadoRef.current === clave) return;
    ultimoClientePredAplicadoRef.current = clave;

    let cancelled = false;
    (async () => {
      try {
        const c = await clienteService.getCliente(nroPred, OPTIONS_CLIENTE_OPERATIVO);
        if (cancelled || !c) return;
        handleClienteSelect(mergeClientePosDesdeApi(c));
      } catch (err) {
        if (esErrorClienteNoOperativo(err)) {
          // No reintentar ni seleccionar: el predeterminado de la sucursal está inactivo.
          if (!cancelled) {
            enqueueSnackbar(getMensajeClienteNoOperativo(err, nroPred), { variant: 'warning' });
          }
          return;
        }
        ultimoClientePredAplicadoRef.current = '';
      }
    })();

    return () => {
      cancelled = true;
    };
    // handleClienteSelect se omite a propósito: no reaplicar al cambiar líneas del documento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    esFacturaPosNueva,
    clientePredeterminado.loading,
    clientePredeterminado.configurado,
    clientePredeterminado.nroCliente,
    nroSuc,
    clientePredApplyTick,
    enqueueSnackbar,
  ]);

  const handleClienteClear = useCallback(() => {
    if (lineasBloqueanEdicionCliente(lineas, cliente?.nroCliente ?? 0)) {
      enqueueSnackbar(MSG_CLIENTE_BLOQUEADO_POR_MASTER, { variant: 'warning' });
      return;
    }
    setCliente(null);
    setFormaDePago(modoRescate ? 'CTA.CTE.' : '');
    setTransporte('');
    setPorcDesc(0);
    setPorcDesc2(0);
    setPorcDesc3(0);
  }, [lineas, cliente?.nroCliente, enqueueSnackbar, modoRescate]);

  const puedeRepetirUltimoPedido =
    !modoVerPos
    && !modoDesdePedido
    && !modoDesdeCotizacion
    && !modoDesdeFacturaOrigen
    && !modoDesdeOrdenTrabajo;

  const puedeCopiarUltimasFacturas = puedeRepetirUltimoPedido;

  const handleRepetirUltimoPedido = useCallback(async () => {
    const nroCliente = Number(cliente?.nroCliente) || 0;
    if (nroCliente <= 0) {
      enqueueSnackbar('Seleccioná un cliente para usar esta opción.', { variant: 'warning' });
      return;
    }
    setLoadingRepetirUltimoPedido(true);
    try {
      const nroLista = obtenerCodigoListaCliente(cliente) || undefined;
      const result = await construirLineasDesdeUltimoPedido({ nroCliente, nroLista });
      if (!result.ok) {
        enqueueSnackbar(result.message, {
          variant: result.reason === 'sin_historial' ? 'info' : 'warning',
        });
        return;
      }
      setPedidoOrigenTotales(null);
      setLineas(result.lineas);
      setProductosIvaCache((prev) => {
        const next = { ...prev, ...result.productosIva };
        productosIvaCacheRef.current = next;
        return next;
      });
      const omitidasMsg =
        result.omitidas > 0
          ? ` (${result.omitidas} línea${result.omitidas > 1 ? 's' : ''} omitida${result.omitidas > 1 ? 's' : ''})`
          : '';
      enqueueSnackbar(
        `Último pedido aplicado: ${result.lineas.length} producto${result.lineas.length > 1 ? 's' : ''}${omitidasMsg}.`,
        { variant: 'success' },
      );
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e) || 'No se pudo repetir el último pedido.', {
        variant: 'error',
      });
    } finally {
      setLoadingRepetirUltimoPedido(false);
    }
  }, [cliente, enqueueSnackbar]);

  const handleCopiarDesdeFactura = useCallback(async (factura: FacturaVenta) => {
    const nroFormulario = Number(factura?.nroFormulario) || 0;
    if (nroFormulario <= 0) {
      enqueueSnackbar('No se indicó una factura válida.', { variant: 'warning' });
      return;
    }

    const hayLineasConArticulo = lineas.some((l) => String(l.codigoArticulo ?? '').trim() !== '');
    if (hayLineasConArticulo) {
      const ok = window.confirm(
        'Ya hay artículos en el documento. ¿Reemplazarlos con los de la factura seleccionada?',
      );
      if (!ok) return;
    }

    try {
      const nroLista = obtenerCodigoListaCliente(cliente) || undefined;
      const result = await construirLineasDesdeFactura({
        nroFormulario,
        nroLista,
        factura,
      });
      if (!result.ok) {
        enqueueSnackbar(result.message, {
          variant: result.reason === 'sin_productos' ? 'warning' : 'info',
        });
        const disFail = result.omitidasDiscontinuadas ?? [];
        if (disFail.length > 0 && result.reason === 'sin_productos') {
          enqueueSnackbar(mensajeOmitidasDiscontinuadas(disFail), {
            variant: 'warning',
            autoHideDuration: 10000,
          });
        }
        return;
      }
      setPedidoOrigenTotales(null);
      setLineas(result.lineas);
      setProductosIvaCache((prev) => {
        const next = { ...prev, ...result.productosIva };
        productosIvaCacheRef.current = next;
        return next;
      });
      enqueueSnackbar(
        `Factura aplicada: ${result.lineas.length} producto${result.lineas.length > 1 ? 's' : ''}.`,
        { variant: 'success' },
      );
      if (result.omitidasDiscontinuadas.length > 0) {
        enqueueSnackbar(mensajeOmitidasDiscontinuadas(result.omitidasDiscontinuadas), {
          variant: 'warning',
          autoHideDuration: 10000,
        });
      } else if (result.omitidasOtras > 0) {
        enqueueSnackbar(
          `${result.omitidasOtras} línea${result.omitidasOtras > 1 ? 's' : ''} omitida${result.omitidasOtras > 1 ? 's' : ''} (sin stock/precio o no disponibles).`,
          { variant: 'info' },
        );
      }
      setUltimasFacturasOpen(false);
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e) || 'No se pudo copiar la factura seleccionada.', {
        variant: 'error',
      });
    }
  }, [cliente, enqueueSnackbar, lineas]);

  const registrarProductoIva = useCallback((codigoArticulo: string, producto: unknown) => {
    const cod = String(codigoArticulo ?? '').trim();
    if (!cod || !producto) return;
    setProductosIvaCache((prev) => {
      if (prev[cod] === producto) return prev;
      const next = { ...prev, [cod]: producto };
      productosIvaCacheRef.current = next;
      return next;
    });
  }, []);

  /** Precarga productos (alícuotas IVA) en cache; no descarta merges útiles. */
  const asegurarProductosIvaEnCache = useCallback(async (codigos: string[]) => {
    const unique = Array.from(
      new Set(codigos.map((c) => String(c ?? '').trim()).filter((c) => c.length > 0)),
    );
    if (unique.length === 0) return;

    const missing = unique.filter((c) => !productosIvaCacheRef.current[c]);
    if (missing.length === 0) return;

    const fetched: Record<string, unknown> = {};
    await Promise.all(
      missing.map(async (codigo) => {
        try {
          const producto = await productoService.getProducto(codigo);
          if (producto) fetched[codigo] = producto;
        } catch {
          /* sin producto */
        }
      }),
    );
    if (Object.keys(fetched).length === 0) return;
    setProductosIvaCache((prev) => {
      const next = { ...prev, ...fetched };
      productosIvaCacheRef.current = next;
      return next;
    });
  }, []);

  const recalcularDescuentosContexto = (cli: POSCliente | null) => {
    if (!cli || modoVerSoloLectura) return;
    const seq = ++clienteBonifSeqRef.current;
    void recalcularDescuentosVenta(
      cli,
      { codigoLista: obtenerCodigoListaCliente(cli), nroVendedor: cli.nroVendedor ?? null },
      lineas,
    ).then(({ cabecera, porLinea }) => {
      if (seq !== clienteBonifSeqRef.current) return;
      setPorcDesc(cabecera.porcDesc);
      setPorcDesc2(cabecera.porcDesc2);
      setPorcDesc3(cabecera.porcDesc3);
      setLineas((prev) =>
        prev.map((l) => {
          const porc = porLinea.get(l.id);
          if (porc == null || l.descuentoManual || porc === l.descuento) return l;
          const updated = { ...l, descuento: porc, importeImportadoPdf: undefined };
          updated.subtotal = calcularSubtotal(updated);
          return updated;
        }),
      );
    });
  };

  const handleAgregarProducto = useCallback((item: SuggestionItem) => {
    setPedidoOrigenTotales(null);
    const cod = String(item.codigoArticulo ?? '').trim();
    if (cod && item.productoIva) {
      registrarProductoIva(cod, item.productoIva);
    }
    const cantidad = item.cantidad && item.cantidad > 0 ? item.cantidad : 1;
    const id = genId();
    const umInicial = String(item.um ?? '').trim();
    const precioInicial = Number(item.precioUnitario) || 0;
    const stockInicial = item.stockDisponible ?? null;
    const loteFlagsInicial = extractLoteFlagsFromProducto(item.productoIva);
    setLineas((prev) => [
      ...prev,
      {
        id,
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad,
        precioUnitario: precioInicial,
        descuento: 0,
        subtotal: calcularSubtotal({ cantidad, precioUnitario: precioInicial, descuento: 0 }),
        um: umInicial,
        stockDisponible: stockInicial,
        controlLoteSerie: loteFlagsInicial.controlLoteSerie,
        requiereVencimiento: loteFlagsInicial.requiereVencimiento,
        lotes: undefined,
      },
    ]);
    if (!cod) return;
    void (async () => {
      try {
        const producto = await productoService.getProducto(cod);
        registrarProductoIva(cod, producto);
        const detalle = await productoService.getEquivalenciasUmDetalle(cod);
        const loteFlags = extractLoteFlagsFromProducto(producto);

        let precioBase = obtenerPrecioBaseProducto(producto);
        const nroListaCli = obtenerCodigoListaCliente(cliente) || '';
        if (nroListaCli) {
          try {
            const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(nroListaCli, cod);
            const res = resolverPrecioDesdeLista(producto, reg);
            if (res.tipo !== 'sinPrecio') precioBase = res.precioLista;
          } catch {
            /* catálogo */
          }
        }

        const r = resolverUmYPrecioDesdeDetalle(producto, precioBase, 'factura', detalle);
        const umFinal = r.um || umInicial;
        const precioFinal = round2(r.precioUnitario);
        const stockFinal = await fetchStockDisponibleEnUm(cod, umFinal, detalle, producto);
        const porcDesc = await resolverPorcDescLinea(
          contextoDescuentoDesdeCliente(cliente, { codigoLista: nroListaCli }),
          producto,
          cantidad,
          umFinal,
        );
        setLineas((prev) =>
          prev.map((l) => {
            if (l.id !== id) return l;
            if (l.descuentoManual) {
              const kept = {
                ...l,
                um: umFinal,
                precioUnitario: precioFinal,
                stockDisponible: stockFinal ?? l.stockDisponible ?? stockInicial,
                controlLoteSerie: loteFlags.controlLoteSerie,
                requiereVencimiento: loteFlags.requiereVencimiento,
              };
              kept.subtotal = calcularSubtotal(kept);
              return kept;
            }
            const updated = {
              ...l,
              um: umFinal,
              precioUnitario: precioFinal,
              stockDisponible: stockFinal ?? l.stockDisponible ?? stockInicial,
              descuento: porcDesc,
              descuentoManual: false,
              controlLoteSerie: loteFlags.controlLoteSerie,
              requiereVencimiento: loteFlags.requiereVencimiento,
            };
            updated.subtotal = calcularSubtotal(updated);
            return updated;
          }),
        );
      } catch {
        /* conservar precio/UM del buscador */
      }
    })();
  }, [registrarProductoIva, cliente]);

  const handleLineaChange = useCallback(
    (id: string, field: keyof POSLinea, value: string | number) => {
      setPedidoOrigenTotales(null);
      setLineas((prev) =>
        prev.map((l) => {
          if (l.id !== id) return l;
          const updated = { ...l, [field]: value };
          if (field === 'descuento') updated.descuentoManual = true;
          updated.subtotal = calcularSubtotal(updated);
          return updated;
        }),
      );
    },
    [],
  );

  const handleLineaFilled = useCallback((id: string, data: Partial<POSLinea>) => {
    setPedidoOrigenTotales(null);
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleEliminarLinea = useCallback((id: string) => {
    setPedidoOrigenTotales(null);
    setLineas((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const handleDuplicarLinea = useCallback((id: string) => {
    setPedidoOrigenTotales(null);
    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  }, []);

  const handleAgregarLineaVacia = useCallback(() => {
    setPedidoOrigenTotales(null);
    setLineas((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '', notas: '' },
    ]);
  }, []);

  const pendienteYaEnLineas = useCallback(
    (p: VentaPendienteMaster) =>
      lineas.some(
        (l) =>
          l.nroFormularioMaster === p.nroFormularioMaster &&
          l.nroItemMaster === p.nroItemMaster,
      ),
    [lineas],
  );

  const pendientesFiltrados = useMemo(() => {
    const porFecha = filtrarPendientesPorFechaEmision(
      pendientes,
      pendientesFechaDesde,
      pendientesFechaHasta,
    );
    const porEstado = filtrarPendientesPorEstado(porFecha, pendientesFiltroEstado);
    return filtrarPendientesPorArticulo(porEstado, pendientesBusquedaObra);
  }, [pendientes, pendientesFechaDesde, pendientesFechaHasta, pendientesFiltroEstado, pendientesBusquedaObra]);

  const pendientesPagina = useMemo(() => {
    const start = pendientesPage * pendientesRowsPerPage;
    return pendientesFiltrados.slice(start, start + pendientesRowsPerPage);
  }, [pendientesFiltrados, pendientesPage, pendientesRowsPerPage]);

  useEffect(() => {
    setPendientesPage(0);
  }, [pendientesFechaDesde, pendientesFechaHasta, pendientesFiltroEstado, pendientesBusquedaObra]);

  useEffect(() => {
    setPendientesPage((prev) => {
      const maxPage = Math.max(0, Math.ceil(pendientesFiltrados.length / pendientesRowsPerPage) - 1);
      return prev > maxPage ? maxPage : prev;
    });
  }, [pendientesRowsPerPage, pendientes.length]);

  const handlePendientesPageChange = useCallback((_: unknown, newPage: number) => {
    setPendientesPage(newPage);
  }, []);

  const handlePendientesRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPendientesRowsPerPage(parseInt(event.target.value, 10));
    setPendientesPage(0);
  }, []);

  const openBuscarPendientes = useCallback(async () => {
    if (!cliente?.nroCliente || cliente.nroCliente <= 0) return;
    setOpenPendientes(true);
    setPendientesFechaDesde('');
    setPendientesFechaHasta('');
    setPendientesBusquedaObra('');
    setPendientesFiltroEstado('');
    setPendientesPage(0);
    setLoadingPendientes(true);
    try {
      const [dataVentas, dataOrdenesTrabajo] = await Promise.all([
        ventasSeguimientoService.getPendientesMaster(cliente.nroCliente, true),
        ordenReparacionService.getPendientesFacturacion(cliente.nroCliente).catch(() => []),
      ]);
      const filtrados = [...(dataVentas || []), ...(dataOrdenesTrabajo || [])].filter(
        (p) => Number(p.pendienteFacturar) > 0,
      );
      const rows = await enrichPendientesConNotasPedido(filtrados);
      const visibles = rows.filter((p) => !pendienteYaEnLineas(p));
      setPendientes(visibles);
      void asegurarProductosIvaEnCache(visibles.map((p) => p.codigoArticulo || ''));
    } catch {
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  }, [cliente?.nroCliente, pendienteYaEnLineas, asegurarProductosIvaEnCache]);

  const agregarPendiente = useCallback(async (p: VentaPendienteEnriquecido) => {
    if (pendienteYaEnLineas(p)) {
      enqueueSnackbar('Ese pendiente ya fue agregado', { variant: 'info' });
      return;
    }
    const lineaBase = lineaPosDesdePendiente(p, Number(cliente?.nroCliente) || 0);
    const linea = await ajustarLineaPendienteAUmFactura(lineaBase);
    void asegurarProductosIvaEnCache([linea.codigoArticulo]);
    setPedidoOrigenTotales(null);
    setLineas((prev) => [...prev, linea]);
    setPendientes((prev) =>
      prev.filter(
        (row) =>
          !(
            row.nroFormularioMaster === p.nroFormularioMaster &&
            row.nroItemMaster === p.nroItemMaster
          ),
      ),
    );
    enqueueSnackbar('Pendiente agregado correctamente', { variant: 'success' });
  }, [pendienteYaEnLineas, cliente?.nroCliente, enqueueSnackbar, asegurarProductosIvaEnCache]);

  useEffect(() => {
    if (!modoDesdePendienteInicial || pendingApplied || !ventaPendienteInicial) return;

    let cancelled = false;
    (async () => {
      const pBase = ventaPendienteInicial;
      const nroCli = Number(pBase.nroCliente ?? 0);

      if (nroCli > 0) {
        try {
          const c = await clienteService.getCliente(nroCli);
          if (cancelled) return;
          const merged = mergeClientePosDesdeApi(c);
          const nm =
            resolverNombreCliente(c) ||
            String(pBase.nombreCliente ?? '').trim() ||
            String(merged.nombre ?? '').trim() ||
            `Cliente #${nroCli}`;
          setCliente({ ...merged, nroCliente: c.nroCliente, nombre: nm, razonSocial: nm });
          setFechaVencim(
            toLocalDateStringYMD(
              calcularFechaVencimDesdeVtoComprob(parseLocalDate(fechaEmision), merged.vtoComprob ?? null),
            ),
          );
          const formaPagoPendiente = resolverFormaDePagoCliente(merged);
          if (formaPagoPendiente) setFormaDePago(formaPagoPendiente);
        } catch {
          if (!cancelled && nroCli > 0) {
            const nm = String(pBase.nombreCliente ?? '').trim() || `Cliente #${nroCli}`;
            setCliente({ nroCliente: nroCli, nombre: nm, razonSocial: nm });
          }
        }
      }

      if (cancelled) return;

      const [pEnriquecido] = await enrichPendientesConNotasPedido([pBase]);
      if (cancelled) return;

      const p = pEnriquecido ?? pBase;
      const lineaBase = lineaPosDesdePendiente(p, nroCli);
      const linea = await ajustarLineaPendienteAUmFactura(lineaBase);
      await asegurarProductosIvaEnCache([linea.codigoArticulo]);
      if (cancelled) return;

      setLineas([linea]);
      setPendingApplied(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [modoDesdePendienteInicial, pendingApplied, ventaPendienteInicial, fechaEmision, asegurarProductosIvaEnCache]);

  const agregarTodosPendientes = useCallback(async () => {
    if (agregandoTodosRef.current) return;
    if (!pendientesFiltrados.length) {
      return;
    }

    agregandoTodosRef.current = true;
    setAgregandoTodos(true);
    // Permite pintar el botón deshabilitado / "Agregando…" antes del trabajo pesado.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    try {
      const keysExistentes = new Set(
        lineas.map((l) => `${l.nroFormularioMaster}-${l.nroItemMaster}`),
      );
      const nuevasLineas: POSLinea[] = [];
      let agregadas = 0;
      let omitidas = 0;
      const pendientesAAgregar = [...pendientesFiltrados];

      for (const p of pendientesAAgregar) {
        const key = `${p.nroFormularioMaster}-${p.nroItemMaster}`;
        if (keysExistentes.has(key)) {
          omitidas += 1;
          continue;
        }
        const lineaBase = lineaPosDesdePendiente(p, Number(cliente?.nroCliente) || 0);
        const linea = await ajustarLineaPendienteAUmFactura(lineaBase);
        nuevasLineas.push(linea);
        keysExistentes.add(key);
        agregadas += 1;
      }

      if (agregadas === 0) {
        enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
        return;
      }

      void asegurarProductosIvaEnCache(nuevasLineas.map((l) => l.codigoArticulo));
      setPedidoOrigenTotales(null);
      setLineas((prev) => [...prev, ...nuevasLineas]);
      setPendientes((prev) =>
        prev.filter(
          (row) =>
            !pendientesAAgregar.some(
              (p) =>
                p.nroFormularioMaster === row.nroFormularioMaster &&
                p.nroItemMaster === row.nroItemMaster,
            ),
        ),
      );
      enqueueSnackbar(
        omitidas > 0
          ? `Se agregaron ${agregadas} líneas. ${omitidas} quedaron omitidas.`
          : `Se agregaron ${agregadas} líneas desde pendientes.`,
        { variant: 'success' },
      );
    } finally {
      agregandoTodosRef.current = false;
      setAgregandoTodos(false);
    }
  }, [
    pendientesFiltrados,
    lineas,
    cliente?.nroCliente,
    enqueueSnackbar,
    asegurarProductosIvaEnCache,
  ]);

  useEffect(() => {
    if (!pendienteHiloInfo) {
      setPendienteHiloItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPendienteHilo(true);
      try {
        const data = await ventasSeguimientoService.getHilo(
          pendienteHiloInfo.nroFormularioMaster,
          pendienteHiloInfo.nroItemMaster,
        );
        if (!cancelled) {
          setPendienteHiloItems(data.comprobantes || []);
        }
      } catch {
        if (!cancelled) {
          setPendienteHiloItems([]);
          enqueueSnackbar('No se pudo cargar el detalle del hilo', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingPendienteHilo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendienteHiloInfo, enqueueSnackbar]);

  const handlePorcDescChange = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setPedidoOrigenTotales(null);
    if (field === 'porcDesc') setPorcDesc(v);
    else if (field === 'porcDesc2') setPorcDesc2(v);
    else setPorcDesc3(v);
  };

  const handleTipoDescChange = (field: 'tipoDesc' | 'tipoDesc2' | 'tipoDesc3', value: 'P' | 'M') => {
    setPedidoOrigenTotales(null);
    if (field === 'tipoDesc') setTipoDesc(value);
    else if (field === 'tipoDesc2') setTipoDesc2(value);
    else setTipoDesc3(value);
  };

  const handleImporteDescChange = (
    field: 'importeDesc' | 'importeDesc2' | 'importeDesc3',
    value: number,
  ) => {
    setPedidoOrigenTotales(null);
    const v = Number.isFinite(value) ? Math.max(0, value) : 0;
    if (field === 'importeDesc') setImporteDesc(v);
    else if (field === 'importeDesc2') setImporteDesc2(v);
    else setImporteDesc3(v);
  };

  const aplicarDescTramo = (base: number, tipo: string | undefined, pct: number, importe: number) => {
    if (String(tipo || 'P').toUpperCase() === 'M') {
      return Math.max(0, base - Math.min(base, Math.max(0, importe || 0)));
    }
    return aplicarPorcentajeDescuento(base, pct || 0);
  };

  /** Al cambiar condición IVA, invalidar totales/precios con IVA del pedido origen. */
  const handleEmisionTipoIvaChange = useCallback((v: string) => {
    setPedidoOrigenTotales(null);
    setCliente((p) => (p ? { ...p, tipoIva: v } : null));
    setLineas((prev) =>
      prev.map((l) => {
        if (l.precioUnitarioFinal == null && l.subtotalFinal == null) return l;
        const { precioUnitarioFinal: _pf, subtotalFinal: _sf, ...rest } = l;
        return rest;
      }),
    );
  }, []);

  const tipoIvaEmision = String(
    cliente?.tipoIva ?? facturaVerPos?.tipoIva ?? pedidoOrigenTotales?.tipoIva ?? '',
  ).trim().toUpperCase();
  const preciosConIva = esTipoIvaPreciosConIva(tipoIvaEmision);
  const condIvaEmisor = useMemo(() => {
    const id = String(idCompania ?? '').trim();
    return companias.find((c) => c.idCompania === id)?.condIva;
  }, [companias, idCompania]);

  useEffect(() => {
    const codigos = Array.from(
      new Set(
        lineas
          .map((l) => String(l.codigoArticulo ?? '').trim())
          .filter((c) => c.length > 0),
      ),
    );
    if (codigos.length === 0) return;
    // Red de seguridad: merges aditivos; no cancelar resultados útiles al cambiar lineas.
    void asegurarProductosIvaEnCache(codigos);
  }, [lineas, asegurarProductosIvaEnCache]);

  // ── Totals ───────────────────────────────────────────────────
  const subtotalNeto = lineas.reduce((acc, l) => acc + l.subtotal, 0);
  const baseAfter1 = aplicarDescTramo(subtotalNeto, tipoDesc, porcDesc, importeDesc);
  const baseAfter2 = aplicarDescTramo(baseAfter1, tipoDesc2, porcDesc2, importeDesc2);
  const baseAfter3 = aplicarDescTramo(baseAfter2, tipoDesc3, porcDesc3, importeDesc3);
  const subtotalConDesc = baseAfter3;

  const totalesIva = useMemo(() => {
    const recalc = calcularTotalesIvaVenta({
      lineas: lineas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        subtotal: l.subtotal,
      })),
      subtotalNeto,
      subtotalConDesc,
      tipoIva: tipoIvaEmision,
      productosCache: productosIvaCache,
      condIvaEmisor,
    });

    if (modoVerSoloLectura && facturaVerPos && (facturaVerPos.importeTotal ?? 0) > 0) {
      const importeTotal = facturaVerPos.importeTotal ?? 0;
      const importeSinIva = facturaVerPos.importeSinIva ?? recalc.subtotalConDesc;
      const montoIVA = facturaVerPos.ivainscripto ?? recalc.montoIVA;
      const impuestosArray =
        recalc.impuestosArray.length > 0
          ? recalc.impuestosArray
          : montoIVA > 0 && importeSinIva > 0
            ? [{ tasa: round2((montoIVA / importeSinIva) * 100), monto: montoIVA }]
            : [];

      return {
        subtotalConDesc: importeSinIva,
        impuestosArray,
        montoIVA,
        total: importeTotal,
      };
    }

    if (pedidoOrigenTotales && (pedidoOrigenTotales.importeTotal ?? 0) > 0) {
      const importeTotal = pedidoOrigenTotales.importeTotal ?? 0;
      const importeSinIva = pedidoOrigenTotales.importeSinIva ?? recalc.subtotalConDesc;
      const montoIVA = pedidoOrigenTotales.ivainscripto ?? recalc.montoIVA;
      const impuestosArray =
        recalc.impuestosArray.length > 0
          ? recalc.impuestosArray
          : montoIVA > 0 && importeSinIva > 0
            ? [{ tasa: round2((montoIVA / importeSinIva) * 100), monto: montoIVA }]
            : [];

      return {
        subtotalConDesc: importeSinIva,
        impuestosArray,
        montoIVA,
        total: importeTotal,
      };
    }

    return recalc;
  }, [
    lineas,
    subtotalNeto,
    subtotalConDesc,
    tipoIvaEmision,
    productosIvaCache,
    condIvaEmisor,
    modoVerSoloLectura,
    facturaVerPos,
    pedidoOrigenTotales,
  ]);

  const { impuestosArray: impuestos, montoIVA: iva, total } = totalesIva;

  const totalesDisplay = useMemo(() => {
    const totalesPersistidos = modoVerSoloLectura ? facturaVerPos : pedidoOrigenTotales;

    if (totalesPersistidos) {
      const sumSubtotalFinal = round2(
        lineas.reduce((acc, l) => acc + (l.subtotalFinal ?? 0), 0),
      );
      const subtotalDisplay = preciosConIva
        ? sumSubtotalFinal > 0
          ? sumSubtotalFinal
          : round2((totalesPersistidos.importeSinIva ?? 0) + (totalesPersistidos.ivainscripto ?? 0))
        : subtotalNeto;
      const after1 = aplicarDescTramo(subtotalDisplay, tipoDesc, porcDesc, importeDesc);
      const importeDesc1Display = round2(subtotalDisplay - after1);
      const after2 = aplicarDescTramo(after1, tipoDesc2, porcDesc2, importeDesc2);
      const importeDesc2Display = round2(after1 - after2);
      const after3 = aplicarDescTramo(after2, tipoDesc3, porcDesc3, importeDesc3);
      const importeDesc3Display = round2(after2 - after3);
      const totalDisplay = after3;

      return {
        subtotalDisplay,
        importeDesc1Display,
        importeDesc2Display,
        importeDesc3Display,
        totalDisplay,
      };
    }

    const subtotalDisplay = preciosConIva
      ? round2(
          lineas.reduce((acc, l) => {
            const codigo = String(l.codigoArticulo ?? '').trim();
            const mult = multiplicadorIvaProducto(productosIvaCache[codigo], tipoIvaEmision, condIvaEmisor);
            return acc + subtotalLineaConIvaDisplay(l.precioUnitario, l.cantidad, l.descuento, mult);
          }, 0),
        )
      : subtotalNeto;
    const after1 = aplicarDescTramo(subtotalDisplay, tipoDesc, porcDesc, importeDesc);
    const importeDesc1Display = round2(subtotalDisplay - after1);
    const after2 = aplicarDescTramo(after1, tipoDesc2, porcDesc2, importeDesc2);
    const importeDesc2Display = round2(after1 - after2);
    const after3 = aplicarDescTramo(after2, tipoDesc3, porcDesc3, importeDesc3);
    const importeDesc3Display = round2(after2 - after3);
    const totalDisplay = after3;

    return {
      subtotalDisplay,
      importeDesc1Display,
      importeDesc2Display,
      importeDesc3Display,
      totalDisplay,
    };
  }, [
    modoVerSoloLectura,
    facturaVerPos,
    pedidoOrigenTotales,
    preciosConIva,
    lineas,
    productosIvaCache,
    tipoIvaEmision,
    condIvaEmisor,
    subtotalNeto,
    porcDesc,
    porcDesc2,
    porcDesc3,
    tipoDesc,
    tipoDesc2,
    tipoDesc3,
    importeDesc,
    importeDesc2,
    importeDesc3,
  ]);

  const totalMostrado = truncToDecimals(
    preciosConIva ? (totalesDisplay.totalDisplay ?? total) : total,
    2,
  );

  const totalArcaRescate = modoRescate ? parseArcaImporte(afipRescate?.importeTotal) : null;
  const diffTotalRescate =
    totalArcaRescate != null
      ? Math.abs(truncToDecimals(totalMostrado, 2) - truncToDecimals(totalArcaRescate, 2))
      : null;
  const totalRescateCierra = diffTotalRescate == null || diffTotalRescate <= 0.01;

  const posCobro = usePosCobro({
    total: totalMostrado,
    formaDePago,
    contadoPagoObligatorio,
    nroCliente: cliente?.nroCliente,
    nroSuc,
    fechaEmision,
    idCompania,
  });

  useEffect(() => {
    let cancelled = false;
    configuracionService.isContadoPagoObligatorio().then((obligatorio) => {
      if (!cancelled) setContadoPagoObligatorio(obligatorio);
    });
    configuracionService.getClienteSuspendidoBloqueoFactura().then((modo) => {
      if (!cancelled) setBloqueoClienteSuspendido(modo);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!posCobro.cobroRequerido) {
      setCobroIncompletoAlEmitir(false);
      return;
    }
    if (!posCobro.validarCobro()) {
      setCobroIncompletoAlEmitir(false);
    }
  }, [
    posCobro.cobroRequerido,
    posCobro.lineasCobro,
    posCobro.sumaCobrada,
    posCobro.restante,
    posCobro.grupoCaja,
    posCobro.validarCobro,
    formaDePago,
    total,
  ]);

  const comprobanteEmitidoId = formatComprobanteEmitido(facturaEmitida);
  const comprobanteReciboId = formatComprobanteEmitido(reciboEmitido);

  const puedeGenerarAsiento =
    modoVerSoloLectura &&
    superAdmin &&
    (facturaVerPos?.importeTotal ?? 0) > 0 &&
    (facturaVerPos?.nroFormulario ?? 0) > 0;

  const ejecutarGenerarAsiento = async () => {
    const id = facturaVerPos?.nroFormulario;
    if (!id) return;
    setOpenGenerarAsientoDialog(false);
    setGenerandoAsiento(true);
    try {
      const r = await facturaVentaService.regenerarAsientoFacturaVenta(id);
      enqueueSnackbar(r.message ?? `Asiento generado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      setFacturaVerPos((prev) =>
        prev ? { ...prev, nroAsiento: r.nroAsiento } : prev,
      );
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo generar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setGenerandoAsiento(false);
    }
  };

  // ── Guardar ──────────────────────────────────────────────────
  const buildPayloadFactura = (): Record<string, unknown> => {
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    const detalles = lineasValidas.map((l, idx) => {
      const nfm = Number(l.nroFormularioMaster);
      const nim = Number(l.nroItemMaster);
      const tieneMaster = Number.isFinite(nfm) && nfm > 0 && Number.isFinite(nim) && nim > 0;
      return {
        nroIdformulario: 0,
        nroItem: idx + 1,
        codigoArticulo: l.codigoArticulo,
        descripcionArticulo: l.descripcionArticulo,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        porcDesc: l.descuento,
        subtotal: l.subtotal,
        um: l.um,
        notas: l.notas,
        ...(facturaDescuentaStock ? { lotes: l.lotes ?? [] } : {}),
        ...(tieneMaster ? { nroFormularioMaster: nfm, nroItemMaster: nim } : {}),
      };
    });

    const payload: Record<string, unknown> = {
      nroFormulario: modoEditarPos ? nroVer : 0,
      nroTipoFormulario: '12',
      nroCliente: cliente!.nroCliente,
      nombre: cliente!.nombre,
      ...((): Record<string, string | undefined> => {
        const doc = String(cliente!.cuit ?? '').trim();
        if (!doc) return {};
        const esCf = tipoIvaEmision === 'C' || tipoIvaEmision === 'CF';
        return esCf ? { dni: doc, cuit: doc } : { cuit: doc };
      })(),
      tipoIva: tipoIvaEmision || undefined,
      tipoIVA: tipoIvaEmision || undefined,
      nroLista: obtenerCodigoListaCliente(cliente!),
      nroVendedor: cliente!.nroVendedor,
      detalles,
      porcDesc,
      porcDesc2,
      porcDesc3,
      tipoDesc,
      tipoDesc2,
      tipoDesc3,
      importeDesc,
      importeDesc2,
      importeDesc3,
      ordenCompra,
      transporte,
      formaEntrega: transporte,
      ubicacion,
      formaDePago,
      fechaEmision: parseLocalDate(fechaEmision.trim()),
      fechaVencim: parseLocalDate(fechaVencim.trim()),
      nroSuc,
      idCompania,
      nroFormularioPedido: nroPedidoOrigen ?? undefined,
      nroFormularioCotizacion: nroCotizacionOrigen ?? undefined,
      nroFormularioOrdenTrabajo: nroOrdenTrabajoOrigen ?? undefined,
      descripcion: observaciones.trim(),
    };
    const dirEnv = String(cliente!.direccionEnvio ?? '').trim();
    if (dirEnv) payload.direccionEnvio = dirEnv;
    const codDirEnv = String(cliente!.codigoDireccionEnvio ?? '').trim();
    if (codDirEnv) payload.codigoDireccionEnvio = codDirEnv;
    const ccEnv = Number(cliente!.codigoCiudadEnvio);
    if (Number.isFinite(ccEnv) && ccEnv > 0) {
      payload.codigoCiudadEnvio = ccEnv;
    }
    return payload;
  };

  const resolverPrecioMinimoLinea = useCallback(
    (l: POSLinea): number | null => {
      if (l.precioMinimo != null && Number.isFinite(Number(l.precioMinimo))) {
        return Number(l.precioMinimo);
      }
      const cod = l.codigoArticulo.trim();
      const prod = productosIvaCache[cod] as { precioMinimo?: number | null } | undefined;
      if (prod?.precioMinimo != null && Number.isFinite(Number(prod.precioMinimo))) {
        return Number(prod.precioMinimo);
      }
      return null;
    },
    [productosIvaCache],
  );

  const handleActualizarFactura = async () => {
    if (!cliente) { enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' }); return; }
    if (!confirmarSiClienteSuspendido()) return;
    if (nroSuc <= 0) { enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' }); return; }
    const lineasCodigoInvalido = lineas.filter(
      (l) => l.codigoArticulo.trim() && !String(l.descripcionArticulo ?? '').trim(),
    );
    if (lineasCodigoInvalido.length > 0) {
      const cod = lineasCodigoInvalido[0].codigoArticulo.trim();
      enqueueSnackbar(`El código "${cod}" no existe en el maestro de artículos`, { variant: 'warning' });
      return;
    }
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    if (lineasValidas.length === 0) {
      enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' });
      return;
    }
    const loteErr = facturaDescuentaStock ? mensajeValidacionLotes(lineasValidas) : null;
    if (loteErr) {
      enqueueSnackbar(loteErr, { variant: 'error' });
      return;
    }
    const errPrecioMin = primerErrorPrecioMinimoLineas(
      lineasValidas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        precioMinimo: resolverPrecioMinimoLinea(l),
      })),
    );
    if (errPrecioMin) {
      enqueueSnackbar(errPrecioMin, { variant: 'error' });
      return;
    }
    advertirLineasConPrecioCero(
      lineasValidas.map((l) => ({
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        porcDesc: l.descuento,
        codigoArticulo: l.codigoArticulo,
        descripcionArticulo: l.descripcionArticulo,
      })),
      enqueueSnackbar,
      'factura',
    );
    if (!fechaEmision?.trim()) { enqueueSnackbar('Ingresá la fecha de emisión', { variant: 'warning' }); return; }
    const fechaVencimError = validarFechasFacturaVenta(
      parseLocalDate(fechaEmision.trim()),
      parseLocalDate(fechaVencim.trim()),
      calcularFechaVencimDesdeVtoComprob(
        parseLocalDate(fechaEmision.trim()),
        cliente?.vtoComprob ?? null,
      ),
    );
    if (fechaVencimError) {
      enqueueSnackbar(fechaVencimError, { variant: 'warning' });
      return;
    }
    const mUb = String(ubicacion ?? '').trim().match(/\((\d+)\)\s*$/);
    const nUb = mUb ? Number(mUb[1]) : NaN;
    if (!Number.isFinite(nUb) || nUb <= 0) {
      enqueueSnackbar('Seleccioná una ubicación / depósito de origen', { variant: 'warning' });
      return;
    }

    const payload = {
      ...buildPayloadFactura(),
      horaEmision: horaEmisionFactura ?? new Date(),
    };
    setSaving(true);
    setSavingPhase('factura');
    try {
      await facturaVentaService.updateFacturaVenta(nroVer, payload as any);
      enqueueSnackbar('Factura actualizada exitosamente', { variant: 'success' });
      navigate('/ventas/facturas');
    } catch (e: any) {
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
      setSavingPhase('idle');
    }
  };

  const ejecutarCreacionFactura = async (
    payload: Record<string, unknown>,
    options?: { emitirConCaePrimero?: boolean; guardarSinCae?: boolean },
  ) => {
    const factura = await facturaVentaService.createFacturaVenta(payload as any, options);
    setFacturaEmitida(factura);
    if (options?.guardarSinCae) {
      enqueueSnackbar('Factura guardada sin CAE. Podés obtener el CAE desde el listado de facturas.', {
        variant: 'warning',
        autoHideDuration: 6000,
      });
    } else {
      enqueueSnackbar('Factura creada exitosamente', { variant: 'success' });
    }
    return factura;
  };

  const ejecutarCobroSobreFactura = async (factura: any) => {
    const nroFormulario = Number(factura?.nroFormulario) || 0;
    if (!nroFormulario) {
      throw new Error('La factura no devolvió nroFormulario para imputar el cobro.');
    }
    const comprobanteLabel =
      formatComprobanteEmitido(factura) || `#${nroFormulario}`;
    const recibo = await posCobro.crearReciboAplicado(
      {
        nroFormulario,
        importeTotal: Number(factura?.importeTotal) || totalMostrado,
        letra: factura?.letra,
        nroSuc: factura?.nroSuc ?? nroSuc,
        nroComprobante: factura?.nroComprobante,
      },
      comprobanteLabel,
    );
    setReciboEmitido(recibo);
    enqueueSnackbar('Recibo de cobro generado', { variant: 'success' });
    return recibo;
  };

  const ejecutarEmisionYCobro = async (
    payload: Record<string, unknown>,
    options?: { emitirConCaePrimero?: boolean; guardarSinCae?: boolean },
  ) => {
    setSavingPhase('factura');
    const factura = await ejecutarCreacionFactura(payload, options);
    // Solo CONTADO/EFECTIVO (cobro habilitado). CTA.CTE. no genera recibo en POS.
    const debeGenerarRecibo =
      posCobro.cobroHabilitado &&
      (posCobro.cobroRequerido || posCobro.lineasCobro.length > 0);
    if (!debeGenerarRecibo) {
      setReciboEmitido(null);
      return factura;
    }
    setSavingPhase('recibo');
    try {
      await ejecutarCobroSobreFactura(factura);
    } catch (e: any) {
      const comprobanteLabel =
        formatComprobanteEmitido(factura) ||
        (factura?.nroFormulario ? `#${factura.nroFormulario}` : '');
      setCobroPendienteDialog({
        factura,
        comprobanteLabel,
        mensaje: extractApiError(e),
      });
    }
    return factura;
  };

  const handleGuardarRescate = async () => {
    if (!afipRescate) return;
    if (!cliente) { enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' }); return; }
    if (!confirmarSiClienteSuspendido()) return;
    if (nroSuc <= 0) { enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' }); return; }
    const lineasCodigoInvalido = lineas.filter(
      (l) => l.codigoArticulo.trim() && !String(l.descripcionArticulo ?? '').trim(),
    );
    if (lineasCodigoInvalido.length > 0) {
      const cod = lineasCodigoInvalido[0].codigoArticulo.trim();
      enqueueSnackbar(
        `El código "${cod}" no existe en el maestro de artículos`,
        { variant: 'warning' },
      );
      return;
    }
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    if (lineasValidas.length === 0) {
      enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' });
      return;
    }
    const errPrecioMinRescate = primerErrorPrecioMinimoLineas(
      lineasValidas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        precioMinimo: resolverPrecioMinimoLinea(l),
      })),
    );
    if (errPrecioMinRescate) {
      enqueueSnackbar(errPrecioMinRescate, { variant: 'error' });
      return;
    }
    advertirLineasConPrecioCero(
      lineasValidas.map((l) => ({
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        porcDesc: l.descuento,
        codigoArticulo: l.codigoArticulo,
        descripcionArticulo: l.descripcionArticulo,
      })),
      enqueueSnackbar,
      'factura',
    );
    if (!fechaEmision?.trim()) { enqueueSnackbar('Ingresá la fecha de emisión', { variant: 'warning' }); return; }
    const fechaVencimError = validarFechasFacturaVenta(
      parseLocalDate(fechaEmision.trim()),
      parseLocalDate(fechaVencim.trim()),
      calcularFechaVencimDesdeVtoComprob(
        parseLocalDate(fechaEmision.trim()),
        cliente?.vtoComprob ?? null,
      ),
    );
    if (fechaVencimError) {
      enqueueSnackbar(fechaVencimError, { variant: 'warning' });
      return;
    }
    const mUb = String(ubicacion ?? '').trim().match(/\((\d+)\)\s*$/);
    const nUb = mUb ? Number(mUb[1]) : NaN;
    if (!Number.isFinite(nUb) || nUb <= 0) {
      enqueueSnackbar('Seleccioná una ubicación / depósito de origen', { variant: 'warning' });
      return;
    }

    setFormaDePago('CTA.CTE.');

    if (totalArcaRescate != null && !totalRescateCierra) {
      enqueueSnackbar(
        `El total de la factura (${formatCurrency(totalMostrado)}) no coincide con el total ARCA (${formatCurrency(totalArcaRescate)}). Ajustá las líneas (±$0,01).`,
        { variant: 'warning', style: { whiteSpace: 'pre-wrap' } },
      );
      return;
    }

    const payload = {
      idWsfe: afipRescate.idWsfe,
      idCompania: afipRescate.idCompania,
      cuitEmisor: afipRescate.cuitEmisor,
      puntoVenta: afipRescate.puntoVenta,
      codigoComprobanteAfip: afipRescate.codigoComprobanteAfip,
      numeroComprobante: afipRescate.numeroComprobante,
      formulario: {
        idCompania: afipRescate.idCompania,
        nroSuc: afipRescate.puntoVenta,
        nroCliente: cliente.nroCliente,
        nombre: cliente.nombre,
        cuit: String(cliente.cuit ?? '').trim() || undefined,
        formaDePago: 'CTA.CTE.',
        nroUbicacionOrigen: nUb,
        descripcion: observaciones.trim() || undefined,
        tipoIva: tipoIvaEmision || undefined,
        nroVendedor: cliente.nroVendedor,
        nroLista: obtenerCodigoListaCliente(cliente),
      },
      items: lineasValidas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        descripcion: l.descripcionArticulo,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        porcDesc: l.descuento,
        um: l.um,
      })),
    };
    setSaving(true);
    setSavingPhase('factura');
    setReciboEmitido(null);
    try {
      const factura = await facturaVentaService.rescateArca(payload);
      setFacturaEmitida(factura);
      enqueueSnackbar('Factura rescatada exitosamente (CAE ARCA aplicado)', { variant: 'success' });
      if (factura && posTicketPrinter.modoImpresionPos === 'ticketera') {
        await posTicketPrinter.imprimirFactura(factura);
      } else if (factura && posTicketPrinter.modoImpresionPos === 'pdf') {
        await posTicketPrinter.imprimirFactura(factura, { forzarPdf: true });
      }
    } catch (e: any) {
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
      setSavingPhase('idle');
    }
  };

  const handleGuardar = async (emitir: boolean) => {
    if (sinPermisoAlta) {
      enqueueSnackbar('No tenés permiso para crear facturas', { variant: 'warning' });
      return;
    }
    if (modoRescate) {
      await handleGuardarRescate();
      return;
    }
    if (modoEditarPos) {
      await handleActualizarFactura();
      return;
    }
    if (!cliente) { enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' }); return; }
    if (!confirmarSiClienteSuspendido()) return;
    if (nroSuc <= 0) { enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' }); return; }
    const lineasCodigoInvalido = lineas.filter(
      (l) => l.codigoArticulo.trim() && !String(l.descripcionArticulo ?? '').trim(),
    );
    if (lineasCodigoInvalido.length > 0) {
      const cod = lineasCodigoInvalido[0].codigoArticulo.trim();
      enqueueSnackbar(
        `El código "${cod}" no existe en el maestro de artículos`,
        { variant: 'warning' },
      );
      return;
    }
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    if (lineasValidas.length === 0) { enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' }); return; }
    const loteErr = facturaDescuentaStock ? mensajeValidacionLotes(lineasValidas) : null;
    if (loteErr) {
      enqueueSnackbar(loteErr, { variant: 'error' });
      return;
    }
    const errPrecioMin = primerErrorPrecioMinimoLineas(
      lineasValidas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        precioMinimo: resolverPrecioMinimoLinea(l),
      })),
    );
    if (errPrecioMin) {
      enqueueSnackbar(errPrecioMin, { variant: 'error' });
      return;
    }
    advertirLineasConPrecioCero(
      lineasValidas.map((l) => ({
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        porcDesc: l.descuento,
        codigoArticulo: l.codigoArticulo,
        descripcionArticulo: l.descripcionArticulo,
      })),
      enqueueSnackbar,
      'factura',
    );
    if (!fechaEmision?.trim()) { enqueueSnackbar('Ingresá la fecha de emisión', { variant: 'warning' }); return; }
    const fechaVencimError = validarFechasFacturaVenta(
      parseLocalDate(fechaEmision.trim()),
      parseLocalDate(fechaVencim.trim()),
      calcularFechaVencimDesdeVtoComprob(
        parseLocalDate(fechaEmision.trim()),
        cliente?.vtoComprob ?? null,
      ),
    );
    if (fechaVencimError) {
      enqueueSnackbar(fechaVencimError, { variant: 'warning' });
      return;
    }
    const mUb = String(ubicacion ?? '').trim().match(/\((\d+)\)\s*$/);
    const nUb = mUb ? Number(mUb[1]) : NaN;
    if (!Number.isFinite(nUb) || nUb <= 0) {
      enqueueSnackbar('Seleccioná una ubicación / depósito de origen', { variant: 'warning' });
      return;
    }

    const errCobro = posCobro.validarCobro();
    if (errCobro) {
      setCobroIncompletoAlEmitir(true);
      enqueueSnackbar(errCobro, { variant: 'warning' });
      return;
    }
    setCobroIncompletoAlEmitir(false);

    const payload = buildPayloadFactura();
    setSaving(true);
    setSavingPhase('factura');
    setReciboEmitido(null);
    try {
      if (emitir) {
        const factura = await ejecutarEmisionYCobro(payload, { emitirConCaePrimero: true });
        if (factura) {
          if (posTicketPrinter.modoImpresionPos === 'ticketera') {
            await posTicketPrinter.imprimirFactura(factura);
          } else if (posTicketPrinter.modoImpresionPos === 'pdf') {
            await posTicketPrinter.imprimirFactura(factura, { forzarPdf: true });
          }
        }
      } else {
        await ejecutarEmisionYCobro(payload, { guardarSinCae: true, emitirConCaePrimero: true });
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 422 && data && data.facturaGrabada === false) {
        setCaeErrorDialog({
          mensaje: String(data.caeError ?? extractApiError(e)),
          caeObs: data.caeObs ? String(data.caeObs) : undefined,
          payload,
        });
        return;
      }
      if (
        (status === 500 || status === 409) &&
        data &&
        data.caeObtenido === true &&
        data.facturaGrabada === false
      ) {
        const caeInfo = [
          data.caeNro ? `CAE: ${data.caeNro}` : null,
          data.nroComprobante != null ? `Nro. comprobante: ${data.nroComprobante}` : null,
          data.caeVencimiento ? `Vto. CAE: ${data.caeVencimiento}` : null,
        ]
          .filter(Boolean)
          .join(' · ');
        setCaeErrorDialog({
          mensaje:
            String(data.caeError ?? extractApiError(e)) +
            (caeInfo ? `\n\n${caeInfo}\nUse Sistema → Compañías → panel ARCA → Rescate.` : ''),
          caeObs: data.caeObs ? String(data.caeObs) : undefined,
          payload,
        });
        return;
      }
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
      setSavingPhase('idle');
    }
  };

  const clienteSeleccionado = Boolean(cliente?.nroCliente);
  const lineasConArticulo = useMemo(
    () =>
      lineas.filter(
        (l) => String(l.codigoArticulo ?? '').trim() && String(l.descripcionArticulo ?? '').trim(),
      ),
    [lineas],
  );
  const puedeAltaFactura = useCan('ventas.facturas.nuevo');
  /** Alta (nueva o rescate) sin permiso: el backend la rechazaría, así que no se permite emitir. */
  const sinPermisoAlta = !modoVerPos && !puedeAltaFactura;
  const puedeEmitir = !modoVerSoloLectura && !saving && clienteSeleccionado && !sinPermisoAlta;
  const dtoLineIndexRef = React.useRef(0);
  /** -1 cerrado | 0 panel totales | 1..3 descuentos */
  const bonifCycleStepRef = React.useRef<-1 | 0 | 1 | 2 | 3>(-1);
  const [totalesExpanded, setTotalesExpanded] = useState(false);
  const [pagoExpanded, setPagoExpanded] = useState(false);
  const [facturarOrigenOpen, setFacturarOrigenOpen] = useState(false);
  const [facturarOrigenTipo, setFacturarOrigenTipo] = useState<FacturarOrigenTipo>('pedido');
  const [atajosDialogOpen, setAtajosDialogOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const pendingCancelRef = useRef<(() => void) | null>(null);
  const [posHotkeyBindings, setPosHotkeyBindings] = useState<PosHotkeyBindings>(() =>
    getPosHotkeyDefaultBindings(),
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await facturaPosAtajosService.getMine();
        if (!cancelled) setPosHotkeyBindings(mergePosHotkeyBindings(res.atajos));
      } catch {
        /* defaults locales */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refs para que F2 (y la barra) no queden con validarCobro / estado de cobro viejos.
  const handleGuardarRef = React.useRef(handleGuardar);
  handleGuardarRef.current = handleGuardar;
  const puedeEmitirRef = React.useRef(puedeEmitir);
  puedeEmitirRef.current = puedeEmitir;
  const posCobroRef = React.useRef(posCobro);
  posCobroRef.current = posCobro;
  const facturaEmitidaRef = React.useRef(facturaEmitida);
  facturaEmitidaRef.current = facturaEmitida;
  const reciboEmitidoRef = React.useRef(reciboEmitido);
  reciboEmitidoRef.current = reciboEmitido;
  const posTicketPrinterRef = React.useRef(posTicketPrinter);
  posTicketPrinterRef.current = posTicketPrinter;
  const handleNuevaFacturaRef = React.useRef<() => void>(() => {});
  const handleImprimirReciboPdfRef = React.useRef<(nroFormularioRecibo: number) => Promise<void>>(
    async () => {},
  );

  const ventaCompletadaOpen = Boolean(facturaEmitida) && !cobroPendienteDialog;
  const {
    ofrecerTrasEmision,
    resetOfferedTracking,
    allowOfferAgain,
    emailUi: postEmisionEmailUi,
  } = usePostEmisionEnvioFacturaEmail();

  // Esperar a que el flujo de cobro asiente: si aparece cobro pendiente, cancelar y re-ofrecer después.
  useEffect(() => {
    if (cobroPendienteDialog) {
      allowOfferAgain();
      return;
    }
    if (!ventaCompletadaOpen) return;
    const nro = Number(facturaEmitida?.nroFormulario) || 0;
    if (nro <= 0) return;
    const timer = window.setTimeout(() => {
      void ofrecerTrasEmision(nro);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    ventaCompletadaOpen,
    cobroPendienteDialog,
    facturaEmitida?.nroFormulario,
    ofrecerTrasEmision,
    allowOfferAgain,
  ]);

  const handleCobroAtajo = useCallback(
    (tipo: 'efectivo' | 'tarjeta' | 'transferencia' | 'otros') => {
      setPagoExpanded(true);
      window.setTimeout(() => {
        document
          .getElementById('pos-pago-panel')
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);

      const err = posCobroRef.current.agregarMedioPorTipo(tipo);
      if (err) {
        enqueueSnackbar(err, { variant: 'warning' });
        return;
      }
      enqueueSnackbar(
        tipo === 'efectivo'
          ? 'Medio efectivo agregado.'
          : tipo === 'transferencia'
            ? 'Medio transferencia agregado.'
            : tipo === 'otros'
              ? 'Medio otros agregado.'
              : 'Medio agregado.',
        { variant: 'success' },
      );
    },
    [enqueueSnackbar],
  );

  const requestCancelConfirm = useCallback((action: () => void) => {
    pendingCancelRef.current = action;
    setCancelConfirmOpen(true);
  }, []);

  const dismissCancelConfirm = useCallback(() => {
    pendingCancelRef.current = null;
    setCancelConfirmOpen(false);
  }, []);

  const acceptCancelConfirm = useCallback(() => {
    const action = pendingCancelRef.current;
    pendingCancelRef.current = null;
    setCancelConfirmOpen(false);
    action?.();
  }, []);

  useEffect(() => {
    if (!cancelConfirmOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        dismissCancelConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [cancelConfirmOpen, dismissCancelConfirm]);

  const posHotkeyHandlers = useMemo<PosHotkeyHandlerMap>(
    () => ({
      dtoLinea: {
        handler: () => {
          if (lineasConArticulo.length === 0) {
            enqueueSnackbar('No hay líneas para editar descuentos', { variant: 'info' });
            return;
          }
          if (dtoLineIndexRef.current >= lineasConArticulo.length) {
            dtoLineIndexRef.current = 0;
          }
          const idx = dtoLineIndexRef.current;
          dtoLineIndexRef.current = (idx + 1) % lineasConArticulo.length;
          const target = lineasConArticulo[idx];
          const rowIdx = lineas.findIndex((l) => l.id === target.id);
          if (rowIdx < 0) return;
          const el = document.querySelector<HTMLInputElement>(
            `[data-row="${rowIdx}"][data-col="descuento"]`,
          );
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          el?.focus();
          el?.select();
        },
        enabled: !modoVerSoloLectura && clienteSeleccionado && lineasConArticulo.length > 0 && !ventaCompletadaOpen,
      },
      bonif: {
        handler: () => {
          const focusBonif = (discountIdx: 0 | 1 | 2) => {
            const el = document.getElementById(
              `pos-bonif-${discountIdx + 1}`,
            ) as HTMLInputElement | null;
            if (!el) {
              enqueueSnackbar('No se encontró el campo de bonificaciones', { variant: 'info' });
              return false;
            }
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            window.setTimeout(() => {
              el.focus();
              el.select();
            }, 50);
            return true;
          };

          const step = bonifCycleStepRef.current;
          if (step < 0) {
            setTotalesExpanded(true);
            bonifCycleStepRef.current = 0;
            window.setTimeout(() => {
              document
                .getElementById('pos-totales-documento')
                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 80);
            return;
          }
          if (step < 3) {
            const next = (step + 1) as 1 | 2 | 3;
            setTotalesExpanded(true);
            if (!focusBonif((next - 1) as 0 | 1 | 2)) return;
            bonifCycleStepRef.current = next;
            return;
          }
          bonifCycleStepRef.current = -1;
          const active = document.activeElement;
          if (active instanceof HTMLElement) {
            active.blur();
          }
          setTotalesExpanded(false);
        },
        enabled: !modoVerSoloLectura && !ventaCompletadaOpen,
      },
      aceptar: {
        handler: () => {
          if (puedeEmitirRef.current) {
            void handleGuardarRef.current(true);
          }
        },
        enabled: puedeEmitir && !ventaCompletadaOpen,
      },
      cancelar: {
        handler: () =>
          requestCancelConfirm(() =>
            navigate(
              modoRescate && afipRescate?.returnTo ? afipRescate.returnTo : '/ventas/facturas',
            ),
          ),
        enabled: !modoVerSoloLectura && !ventaCompletadaOpen,
      },
      factPedidos: {
        handler: () => {
          setFacturarOrigenTipo('pedido');
          setFacturarOrigenOpen(true);
        },
        enabled: !modoVerSoloLectura && puedeAltaFactura && !ventaCompletadaOpen && !modoRescate,
      },
      factPendientes: {
        handler: () => {
          if (!clienteSeleccionado) {
            enqueueSnackbar('Seleccioná un cliente para buscar pendientes', { variant: 'warning' });
            return;
          }
          void openBuscarPendientes();
        },
        enabled: !modoVerSoloLectura && clienteSeleccionado && !ventaCompletadaOpen && !modoRescate,
      },
      factCotizaciones: {
        handler: () => {
          setFacturarOrigenTipo('cotizacion');
          setFacturarOrigenOpen(true);
        },
        enabled: !modoVerSoloLectura && puedeAltaFactura && !ventaCompletadaOpen && !modoRescate,
      },
      'cobro.efectivo': {
        handler: () => handleCobroAtajo('efectivo'),
        enabled: !modoVerSoloLectura && clienteSeleccionado && posCobro.cobroHabilitado && !ventaCompletadaOpen && !modoRescate,
      },
      'cobro.transferencia': {
        handler: () => handleCobroAtajo('transferencia'),
        enabled: !modoVerSoloLectura && clienteSeleccionado && posCobro.cobroHabilitado && !ventaCompletadaOpen && !modoRescate,
      },
      'cobro.tarjeta': {
        handler: () => handleCobroAtajo('tarjeta'),
        enabled: !modoVerSoloLectura && clienteSeleccionado && posCobro.cobroHabilitado && !ventaCompletadaOpen && !modoRescate,
      },
      'cobro.otros': {
        handler: () => handleCobroAtajo('otros'),
        enabled: !modoVerSoloLectura && clienteSeleccionado && posCobro.cobroHabilitado && !ventaCompletadaOpen && !modoRescate,
      },
      'post.listado': {
        handler: () => navigate('/ventas/facturas'),
        enabled: ventaCompletadaOpen,
      },
      'post.imprimirPdf': {
        handler: () => {
          const factura = facturaEmitidaRef.current;
          if (!factura?.nroFormulario) return;
          void posTicketPrinterRef.current.imprimirFactura(factura, { forzarPdf: true });
        },
        enabled:
          ventaCompletadaOpen &&
          Boolean(facturaEmitida?.nroFormulario) &&
          (posTicketPrinter.modoImpresionPos === 'ticketera' ||
            posTicketPrinter.modoImpresionPos === 'pdf'),
      },
      'post.imprimirTicket': {
        handler: () => {
          const factura = facturaEmitidaRef.current;
          if (!factura?.nroFormulario) return;
          void posTicketPrinterRef.current.imprimirFactura(factura);
        },
        enabled:
          ventaCompletadaOpen &&
          Boolean(facturaEmitida?.nroFormulario) &&
          posTicketPrinter.modoImpresionPos === 'ticketera',
      },
      'post.imprimirRecibo': {
        handler: () => {
          const recibo = reciboEmitidoRef.current;
          if (!recibo?.nroFormulario) return;
          void handleImprimirReciboPdfRef.current(Number(recibo.nroFormulario));
        },
        enabled: ventaCompletadaOpen && Boolean(reciboEmitido?.nroFormulario),
      },
      'post.nuevaFactura': {
        handler: () => handleNuevaFacturaRef.current(),
        enabled: ventaCompletadaOpen,
      },
    }),
    [
      lineas,
      lineasConArticulo,
      modoVerSoloLectura,
      modoRescate,
      afipRescate,
      clienteSeleccionado,
      puedeEmitir,
      puedeAltaFactura,
      ventaCompletadaOpen,
      facturaEmitida,
      reciboEmitido,
      posTicketPrinter.modoImpresionPos,
      enqueueSnackbar,
      navigate,
      openBuscarPendientes,
      handleCobroAtajo,
      posCobro.cobroHabilitado,
      requestCancelConfirm,
    ],
  );

  usePosConfigurableHotkeys({
    bindings: posHotkeyBindings,
    handlers: posHotkeyHandlers,
    disabled: modoVerSoloLectura || atajosDialogOpen || cancelConfirmOpen,
    allowWhenDialogOpen: ventaCompletadaOpen
      ? [...POS_HOTKEY_POST_EMISION_IDS]
      : ['dtoLinea'],
  });

  const posHotkeyButtonLabel = useCallback(
    (caption: string, accionId: PosHotkeyAccionId) => {
      const tecla = posHotkeyBindings[accionId];
      return tecla ? `${caption} (${formatPosHotkeyLabel(tecla)})` : caption;
    },
    [posHotkeyBindings],
  );

  const hotkeyBarItems = useMemo((): FormHotkeyBarItem[] => {
    return POS_HOTKEY_ACTIONS.filter((a) => {
      if (!a.showInBar) return false;
      if (!posHotkeyBindings[a.id]) return false;
      if (
        (a.id === 'factPedidos' || a.id === 'factCotizaciones') &&
        !puedeAltaFactura
      ) {
        return false;
      }
      return true;
    }).map((a) => {
      const tecla = posHotkeyBindings[a.id]!;
      const keyLabel = formatPosHotkeyLabel(tecla);
      const action = posHotkeyHandlers[a.id];
      let label = `${keyLabel}-${a.caption}`;
      if (a.id === 'aceptar' && saving) label = 'Procesando...';
      else if (a.id === 'aceptar') label = `${keyLabel}-Aceptar`;
      return {
        id: a.id,
        label,
        tooltip:
          a.id === 'aceptar'
            ? modoEditarPos
              ? 'Actualizar factura'
              : 'Emitir factura'
            : a.id === 'cancelar'
              ? 'Pide confirmación y vuelve al listado de facturas'
              : a.id === 'dtoLinea'
                ? 'Editar descuento línea por línea (vuelve a presionar para la siguiente)'
                : a.tooltip,
        onClick: () => action?.handler(),
        disabled: action?.enabled === false || !action,
        variant: a.variant ?? 'default',
      };
    });
  }, [posHotkeyBindings, posHotkeyHandlers, saving, modoEditarPos, puedeAltaFactura]);

  const atajosLeadingSlot = !modoVerSoloLectura ? (
    <Tooltip title="Configurar atajos de teclado">
      <Button
        size="small"
        variant="text"
        startIcon={<KeyboardIcon />}
        onClick={() => setAtajosDialogOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        Atajos
      </Button>
    </Tooltip>
  ) : null;

  const handleReintentarCobro = async () => {
    if (!cobroPendienteDialog) return;
    const { factura } = cobroPendienteDialog;
    setSaving(true);
    setSavingPhase('recibo');
    try {
      await ejecutarCobroSobreFactura(factura);
      setCobroPendienteDialog(null);
    } catch (e: any) {
      setCobroPendienteDialog((prev) =>
        prev
          ? { ...prev, mensaje: extractApiError(e) }
          : null,
      );
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
      setSavingPhase('idle');
    }
  };

  const handleClosePdfRecibo = useCallback(() => {
    if (pdfReciboUrl) {
      window.URL.revokeObjectURL(pdfReciboUrl);
    }
    setPdfReciboUrl(null);
    setPdfReciboId(null);
    setPdfReciboFileName(null);
  }, [pdfReciboUrl]);

  const handleImprimirReciboPdf = async (nroFormularioRecibo: number) => {
    try {
      const response = await api.get(`/api/tesoreria/recibos/${nroFormularioRecibo}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as { 'content-disposition'?: string })?.['content-disposition'];
      setPdfReciboFileName(getFileNameFromContentDisposition(cd) ?? null);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfReciboUrl(url);
      setPdfReciboId(nroFormularioRecibo);
    } catch {
      enqueueSnackbar('Error al generar el PDF del recibo', { variant: 'error' });
    }
  };

  const handleGuardarSinCaeDesdeDialogo = async () => {
    if (!caeErrorDialog) return;
    setCaeErrorDialog(null);
    const errCobro = posCobro.validarCobro();
    if (errCobro) {
      setCobroIncompletoAlEmitir(true);
      enqueueSnackbar(errCobro, { variant: 'warning' });
      return;
    }
    setCobroIncompletoAlEmitir(false);
    setSaving(true);
    setReciboEmitido(null);
    try {
      const factura = await ejecutarEmisionYCobro(caeErrorDialog.payload, {
        emitirConCaePrimero: true,
        guardarSinCae: true,
      });
      if (factura) {
        if (posTicketPrinter.modoImpresionPos === 'ticketera') {
          await posTicketPrinter.imprimirFactura(factura);
        } else if (posTicketPrinter.modoImpresionPos === 'pdf') {
          await posTicketPrinter.imprimirFactura(factura, { forzarPdf: true });
        }
      }
    } catch (e: any) {
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
      setSavingPhase('idle');
    }
  };

  const nroLista = obtenerCodigoListaCliente(cliente) || undefined;

  const handleNuevaFactura = () => {
    handleClosePdfRecibo();
    posTicketPrinter.closePdfFactura();
    resetOfferedTracking();
    setFacturaEmitida(null);
    setReciboEmitido(null);
    setCobroPendienteDialog(null);
    setCobroIncompletoAlEmitir(false);
    posCobro.resetCobro();
    if (modoRescate) {
      navigate(facturaPosPath(), { replace: true });
      return;
    }
    if (modoVerPos || modoDesdePedido || modoDesdeOrdenTrabajo) {
      navigate('/ventas/facturas/pos', { replace: true });
      return;
    }
    setCliente(null);
    setLineas([]);
    setPorcDesc(0); setPorcDesc2(0); setPorcDesc3(0);
    setFormaDePago(''); setOrdenCompra('');
    setTransporte('');
    setUbicacion('');
    setClientePredApplyTick((t) => t + 1);
    void (async () => {
      try {
        const list = await ubicacionService.getUbicaciones();
        if (!list?.length) return;
        const u = list[0];
        setUbicacion(etiquetaUbicacionSeleccion(u));
      } catch {
        /* sin catálogo */
      }
    })();
    setFechaEmision(toLocalDateStringYMD());
    setObservaciones('');
  };
  handleNuevaFacturaRef.current = handleNuevaFactura;
  handleImprimirReciboPdfRef.current = handleImprimirReciboPdf;

  if ((modoVerPos && loadingFactura) || ((modoDesdePedido || modoDesdeOrdenTrabajo) && loadingPedido)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {modoVerPos ? 'Cargando factura…' : 'Cargando pedido…'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', pb: { xs: 2, sm: 3, md: 10 }, maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (modoVerSoloLectura) {
                navigate('/ventas/facturas');
                return;
              }
              const dest =
                modoRescate && afipRescate?.returnTo
                  ? afipRescate.returnTo
                  : '/ventas/facturas';
              requestCancelConfirm(() => navigate(dest));
            }}
            sx={{ textTransform: 'none' }}
          >
            {modoRescate && afipRescate?.returnTo ? 'Volver al panel ARCA' : 'Facturas de Venta'}
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
            {modoRescate
              ? `Rescate ARCA${tituloPos ? ` ${tituloPos}` : ''}`
              : modoVerPos
              ? `${modoEditarPos ? 'Editar Factura' : 'Ver Factura'}${tituloPos ? ` ${tituloPos}` : ''}`
              : modoDesdePedido
                ? 'Facturar pedido'
                : modoDesdeOrdenTrabajo
                  ? 'Facturar orden de trabajo'
                : modoDesdeCotizacion
                  ? 'Facturar cotización'
                  : modoDesdeFacturaOrigen
                    ? 'Facturar desde factura'
                    : 'Nueva Factura POS'}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {!modoVerSoloLectura && (
            <Tooltip title="Configurar atajos de teclado">
              <Button
                variant="outlined"
                size="small"
                startIcon={<KeyboardIcon />}
                onClick={() => setAtajosDialogOpen(true)}
                sx={{ textTransform: 'none', display: { md: 'none' } }}
              >
                Atajos
              </Button>
            </Tooltip>
          )}
          {modoVerSoloLectura && facturaVerPos?.nroFormulario && posTicketPrinter.modoImpresionPos === 'ticketera' ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                disabled={posTicketPrinter.printing}
                onClick={() =>
                  void posTicketPrinter.imprimirFactura(
                    {
                      nroFormulario: facturaVerPos.nroFormulario,
                      nroSuc,
                      letra: facturaVerPos.letra,
                      ubicacion,
                    },
                    { forzarPdf: true },
                  )
                }
                sx={{ textTransform: 'none' }}
              >
                Imprimir PDF
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                disabled={posTicketPrinter.printing}
                onClick={() =>
                  void posTicketPrinter.imprimirFactura({
                    nroFormulario: facturaVerPos.nroFormulario,
                    nroSuc,
                    letra: facturaVerPos.letra,
                    ubicacion,
                  })
                }
                sx={{ textTransform: 'none' }}
              >
                Imprimir ticket
              </Button>
            </>
          ) : null}
          {modoVerSoloLectura && facturaVerPos?.nroFormulario && posTicketPrinter.modoImpresionPos === 'pdf' ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              disabled={posTicketPrinter.printing}
              onClick={() =>
                void posTicketPrinter.imprimirFactura(
                  {
                    nroFormulario: facturaVerPos.nroFormulario,
                    nroSuc,
                    letra: facturaVerPos.letra,
                    ubicacion,
                  },
                  { forzarPdf: true },
                )
              }
              sx={{ textTransform: 'none' }}
            >
              Imprimir PDF
            </Button>
          ) : null}
        </Stack>

        {modoVerSoloLectura && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {puedeEditarFacturaPos && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(facturaPosEditarPath(nroVer))}
                sx={{ textTransform: 'none' }}
              >
                Editar
              </Button>
            )}
            {superAdmin && (
              <Tooltip
                title={
                  puedeGenerarAsiento
                    ? 'Elimina el/los asiento(s) vinculados y genera uno nuevo con la lógica actual (solo SUPERADMIN).'
                    : 'Se requiere importe total positivo.'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<SyncIcon />}
                    disabled={!puedeGenerarAsiento || generandoAsiento}
                    onClick={() => setOpenGenerarAsientoDialog(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Generar asiento
                  </Button>
                </span>
              </Tooltip>
            )}
            <VerAsientoContableButton
              nroAsiento={facturaVerPos?.nroAsiento}
              variant="outlined"
              color="secondary"
              startIcon={<AccountBalanceOutlinedIcon />}
              sx={{ textTransform: 'none' }}
            />
          </Stack>
        )}
      </Stack>

      {modoRescate && afipRescate && (
        <Alert
          severity={lineas.length > 0 && !totalRescateCierra ? 'error' : 'warning'}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight={600}>
            Rescate ARCA — {tituloPos
              || `${String(afipRescate.puntoVenta).padStart(4, '0')}-${String(afipRescate.numeroComprobante).padStart(8, '0')}`}
            {afipRescate.cae ? ` · CAE ${afipRescate.cae}` : ''}
            {totalArcaRescate != null ? ` · Total ARCA ${formatCurrency(totalArcaRescate)}` : ''}
          </Typography>
          {(afipRescate.impNeto != null && String(afipRescate.impNeto).trim() !== '')
            || (afipRescate.impIva != null && String(afipRescate.impIva).trim() !== '') ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {[
                (() => {
                  const n = parseArcaImporte(afipRescate.impNeto);
                  return n != null ? `Neto ${formatCurrency(n)}` : null;
                })(),
                (() => {
                  const n = parseArcaImporte(afipRescate.impIva);
                  return n != null ? `IVA ${formatCurrency(n)}` : null;
                })(),
              ].filter(Boolean).join(' · ')}
            </Typography>
          ) : null}
          {lineas.length > 0 && !totalRescateCierra && totalArcaRescate != null && (
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              El total del documento ({formatCurrency(totalMostrado)}) no coincide con el total ARCA
              ({formatCurrency(totalArcaRescate)}). Debe cerrar ±$0,01 para grabar el rescate.
            </Typography>
          )}
        </Alert>
      )}

      {sinPermisoAlta && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No tenés permiso para crear facturas. Podés consultar el POS, pero no emitir comprobantes.
        </Alert>
      )}

      {/* ── Layout split ── */}
      <Grid container spacing={2} alignItems="flex-start">
        {/* Columna izquierda ~65% */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2.5}>
            <Box data-tour-id="pos-cliente">
              <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Cliente</Typography>
              <FacturaPOSClientePanel
                cliente={cliente}
                lineas={lineas}
                onClienteSelect={handleClienteSelect}
                onClienteClear={modoVerSoloLectura ? undefined : handleClienteClear}
                readOnly={modoVerSoloLectura}
                formaDePago={formaDePago}
                ordenCompra={ordenCompra}
                onRepetirUltimoPedido={
                  puedeRepetirUltimoPedido ? handleRepetirUltimoPedido : undefined
                }
                loadingRepetirUltimoPedido={loadingRepetirUltimoPedido}
                onUltimasFacturas={
                  puedeCopiarUltimasFacturas
                    ? () => setUltimasFacturasOpen(true)
                    : undefined
                }
                onVerInfo360={
                  cliente?.nroCliente
                    ? () => setInfo360Cliente(cliente.nroCliente)
                    : undefined
                }
                bloqueoClienteSuspendido={bloqueoClienteSuspendido}
              />
            </Box>
            {!modoVerSoloLectura && (
              <Box data-tour-id="pos-busqueda">
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Artículos</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FacturaPOSSearchBar
                      nroLista={nroLista}
                      contextoUm="factura"
                      tipoIvaEmision={tipoIvaEmision}
                      condIvaEmisor={condIvaEmisor}
                      preciosConIva={preciosConIva}
                      onAgregarProducto={handleAgregarProducto}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => void openBuscarPendientes()}
                    disabled={!cliente || cliente.nroCliente <= 0}
                    fullWidth={isMobile}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Buscar pendientes
                  </Button>
                </Box>
              </Box>
            )}
            <Box data-tour-id="pos-lineas">
              {lineas.length > 0 && (
                <Stack direction="row" justifyContent="flex-end" mb={0.75}>
                  <Chip label={`${lineas.length} renglon${lineas.length > 1 ? 'es' : ''}`} size="small" />
                </Stack>
              )}
              <FacturaPOSLineasGrid
                lineas={lineas}
                nroLista={nroLista}
                contextoDescuento={contextoDescuentoDesdeCliente(cliente, {
                  codigoLista: nroLista,
                  nroVendedor: cliente?.nroVendedor,
                })}
                contextoUm="factura"
                tipoIvaEmision={tipoIvaEmision}
                condIvaEmisor={condIvaEmisor}
                productosIvaCache={productosIvaCache}
                preciosConIva={preciosConIva}
                readOnly={modoVerSoloLectura}
                usarPreciosPersistidosEnLineas={
                  (modoVerSoloLectura && (facturaVerPos?.importeTotal ?? 0) > 0) ||
                  Boolean(pedidoOrigenTotales)
                }
                loteAsignacion={
                  (() => {
                    if (!facturaDescuentaStock) return null;
                    const nUb = parseNroUbicacionFromLabel(ubicacion) ?? 0;
                    return nUb > 0 ? { mode: 'egreso' as const, nroUbicacion: nUb } : null;
                  })()
                }
                onLineaChange={handleLineaChange}
                onLineaFilled={handleLineaFilled}
                onProductoIvaLoaded={registrarProductoIva}
                onEliminarLinea={handleEliminarLinea}
                onDuplicarLinea={handleDuplicarLinea}
                onAgregarLineaVacia={handleAgregarLineaVacia}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Panel lateral ~35% */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <FacturaPOSTotalesPanel
              subtotalNeto={subtotalNeto}
              preciosConIva={preciosConIva}
              subtotalDisplay={totalesDisplay.subtotalDisplay}
              importeDesc1Display={totalesDisplay.importeDesc1Display}
              importeDesc2Display={totalesDisplay.importeDesc2Display}
              importeDesc3Display={totalesDisplay.importeDesc3Display}
              porcDesc={porcDesc} porcDesc2={porcDesc2} porcDesc3={porcDesc3}
              onPorcDescChange={handlePorcDescChange}
              tipoDesc={tipoDesc}
              tipoDesc2={tipoDesc2}
              tipoDesc3={tipoDesc3}
              importeDesc={importeDesc}
              importeDesc2={importeDesc2}
              importeDesc3={importeDesc3}
              onTipoDescChange={handleTipoDescChange}
              onImporteDescChange={handleImporteDescChange}
              impuestos={impuestos}
              montoIVA={iva}
              total={totalMostrado}
              observaciones={observaciones}
              onObservacionesChange={setObservaciones}
              cliente={cliente}
              fechaEmision={fechaEmision}
              onFechaEmisionChange={handleFechaEmisionChange}
              fechaVencim={fechaVencim}
              onFechaVencimChange={modoVerSoloLectura ? undefined : handleFechaVencimChange}
              fechaVencimMin={fechaVencimMin}
              fechaVencimHelperText={``}
              idCompania={idCompania}
              onIdCompaniaChange={handleIdCompaniaChange}
              companias={companias}
              loadingCompanias={loadingSuc}
              nroVendedor={cliente?.nroVendedor ?? 0}
              nombreVendedorFallback={cliente?.vendedorInfo?.nombreVendedor ?? undefined}
              onNroVendedorChange={(nro, nombre) => {
                if (!cliente) return;
                const next: POSCliente = !nro
                  ? { ...cliente, nroVendedor: undefined, vendedorInfo: undefined }
                  : { ...cliente, nroVendedor: nro, vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre } };
                setCliente(next);
                recalcularDescuentosContexto(next);
              }}
              codigoLista={nroLista ?? ''}
              onCodigoListaChange={(codigo, descripcion) => {
                if (!cliente) return;
                const c = String(codigo ?? '').trim();
                const next: POSCliente = !c
                  ? { ...cliente, nroLista: undefined, listaPrecio: undefined }
                  : {
                      ...cliente,
                      nroLista: c,
                      listaPrecio: {
                        codigoLista: c,
                        descripcion: descripcion ?? cliente.listaPrecio?.descripcion ?? null,
                      },
                    };
                setCliente(next);
                recalcularDescuentosContexto(next);
              }}
              ordenCompra={ordenCompra} onOrdenCompraChange={setOrdenCompra}
              transporte={transporte} onTransporteChange={setTransporte}
              ubicacion={ubicacion} onUbicacionChange={setUbicacion}
              direccionEnvio={cliente?.direccionEnvio ?? ''}
              onDireccionEnvioChange={(v) =>
                setCliente((p) => (p ? { ...p, direccionEnvio: v.trim() || undefined } : null))
              }
              codigoDireccionEnvio={cliente?.codigoDireccionEnvio ?? ''}
              onCodigoDireccionEnvioChange={(codigo, det) =>
                setCliente((p) => {
                  if (!p) return null;
                  const c = String(codigo ?? '').trim();
                  if (!c) {
                    return { ...p, codigoDireccionEnvio: undefined, codigoCiudadEnvio: undefined };
                  }
                  const dir = String(det?.direccion ?? '').trim();
                  const ccRaw = det?.codigoCiudad;
                  const ccNum =
                    ccRaw == null || (typeof ccRaw === 'string' && String(ccRaw).trim() === '')
                      ? NaN
                      : Number(ccRaw);
                  const codCiudad = Number.isFinite(ccNum) && ccNum > 0 ? ccNum : undefined;
                  return {
                    ...p,
                    codigoDireccionEnvio: c,
                    ...(dir ? { direccionEnvio: dir } : {}),
                    codigoCiudadEnvio: codCiudad,
                  };
                })
              }
              saving={saving}
              onGuardar={handleGuardar}
              cantLineas={lineas.length}
              guardadoBloqueado={guardadoBloqueadoPorClienteSuspendido || sinPermisoAlta}
              lockFiscalHeader={modoRescate}
              dataTourPanelId="pos-totales"
              dataTourEmitId="pos-emitir"
              saveLabel={
                modoRescate
                  ? saving
                    ? 'Grabando rescate…'
                    : 'Grabar rescate'
                  : saving
                  ? modoEditarPos
                    ? 'Actualizando factura…'
                    : savingPhase === 'recibo'
                      ? 'Generando recibo…'
                      : 'Solicitando autorización AFIP…'
                  : modoEditarPos
                    ? 'Actualizar factura'
                    : posCobro.cobroRequerido
                      || (posCobro.cobroHabilitado && posCobro.sumaCobrada > 0)
                      ? 'Emitir y cobrar'
                      : 'Emitir factura'
              }
              saveSubLabel={
                modoRescate
                  ? saving
                    ? 'Validando CAE en ARCA y grabando…'
                    : undefined
                  : saving
                  ? modoEditarPos
                    ? undefined
                    : savingPhase === 'recibo'
                      ? 'Generando recibo de cobro…'
                      : 'Solicitando autorización AFIP…'
                  : undefined
              }
              hideDraftButton
              totalesExpanded={totalesExpanded}
              onTotalesExpandedChange={setTotalesExpanded}
              cobroPanel={
                !modoVerPos && !modoRescate ? (
                  <FacturaPOSPagoPanel
                    total={totalMostrado}
                    clienteSeleccionado={Boolean(cliente?.nroCliente)}
                    formaDePago={formaDePago}
                    onFormaDePagoChange={setFormaDePago}
                    cobroHabilitado={posCobro.cobroHabilitado}
                    cobroRequerido={posCobro.cobroRequerido}
                    mostrarCobroIncompleto={cobroIncompletoAlEmitir}
                    readOnly={modoVerPos}
                    loadingGrupo={posCobro.loadingGrupo}
                    loadingMedios={posCobro.loadingMedios}
                    tieneMedios={posCobro.tieneMedios}
                    grupoNombre={posCobro.grupoCaja?.nombre}
                    puedeCambiarGrupo={posCobro.gruposUsuario.length > 1}
                    onCambiarGrupo={posCobro.abrirSeleccionGrupo}
                    lineasCobro={posCobro.lineasCobro}
                    onEliminarLinea={posCobro.eliminarLineaCobro}
                    sumaCobrada={posCobro.sumaCobrada}
                    restante={posCobro.restante}
                    draft={posCobro.draft}
                    onDraftTipoChange={posCobro.setDraftTipoMedio}
                    onDraftFieldChange={posCobro.setDraftField}
                    onAgregarMedio={posCobro.agregarLineaCobro}
                    puedeAgregarMedio={posCobro.puedeAgregarMedio}
                    mediosEfectivo={posCobro.mediosEfectivo}
                    mediosTarjeta={posCobro.mediosTarjeta}
                    mediosTransferencia={posCobro.mediosTransferencia}
                    mediosOtros={posCobro.mediosOtros}
                    marcasTarjeta={posCobro.marcasTarjeta}
                    expanded={pagoExpanded}
                    onExpandedChange={setPagoExpanded}
                  />
                ) : undefined
              }
              sucursales={sucursalesDeCompania}
              nroSuc={nroSuc}
              onNroSucChange={(n) => {
                sucursalUsuarioEditadaRef.current = true;
                setNroSuc(n);
                const suc = sucursales.find((s) => s.nroSuc === n);
                const idFromSuc = String(suc?.idCompania ?? '').trim();
                if (idFromSuc && idFromSuc !== String(idCompania ?? '').trim()) {
                  setIdCompania(idFromSuc);
                  persistGlobalIdCompania(idFromSuc);
                }
              }}
              loadingSucursales={loadingSucCombined}
              emisionNombre={cliente?.nombre ?? ''}
              onEmisionNombreChange={(v) =>
                setCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : null))
              }
              emisionCuit={cliente?.cuit ?? ''}
              onEmisionCuitChange={(v) => setCliente((p) => (p ? { ...p, cuit: v } : null))}
              emisionTipoIva={cliente?.tipoIva ?? ''}
              onEmisionTipoIvaChange={handleEmisionTipoIvaChange}
              readOnly={modoVerSoloLectura}
              nroInterno={nroVer || facturaVerPos?.nroFormulario || 0}
            />
          </Box>
        </Grid>
      </Grid>
        </CardContent>
      </Card>

      <FormHotkeyBar
          sticky
          items={modoVerSoloLectura ? [] : hotkeyBarItems}
          rightMeta={nroVer || facturaVerPos?.nroFormulario || null}
          leadingSlot={atajosLeadingSlot}
        />
      <ConfirmCancelHotkeyDialog
        open={cancelConfirmOpen}
        onConfirm={acceptCancelConfirm}
        onDismiss={dismissCancelConfirm}
      />

      <FacturaPOSAtajosDialog
        open={atajosDialogOpen}
        onClose={() => setAtajosDialogOpen(false)}
        onSaved={(b) => setPosHotkeyBindings(b)}
        modoImpresionPos={posTicketPrinter.modoImpresionPos}
      />

      <FacturarOrigenDialog
        key={`facturar-origen-${facturarOrigenTipo}`}
        open={facturarOrigenOpen && puedeAltaFactura}
        tipo={facturarOrigenTipo}
        nroCliente={cliente?.nroCliente}
        onClose={() => setFacturarOrigenOpen(false)}
        onSelect={(nroFormulario) => {
          setFacturarOrigenOpen(false);
          navigate(
            facturaPosPath(
              facturarOrigenTipo === 'pedido'
                ? { pedidoId: nroFormulario }
                : { cotizacionId: nroFormulario },
            ),
          );
        }}
      />

      <FacturaPOSUltimasFacturasDialog
        open={ultimasFacturasOpen}
        nroCliente={Number(cliente?.nroCliente) || 0}
        onClose={() => setUltimasFacturasOpen(false)}
        onSelect={handleCopiarDesdeFactura}
      />

      <Dialog
        open={openPendientes}
        onClose={() => {
          if (agregandoTodos) return;
          setOpenPendientes(false);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1.05rem' }}>Buscar pendientes</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {loadingPendientes ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Cargando pendientes...
            </Typography>
          ) : (
            <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 2, mt: 1, alignItems: { xs: 'stretch', sm: 'flex-end' } }}
              flexWrap="wrap"
            >
              <TextField
                placeholder="Artículo"
                value={pendientesBusquedaObra}
                onChange={(e) => setPendientesBusquedaObra(e.target.value)}
                size="small"
                sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
              />
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
                <InputLabel id="pendientes-filtro-estado-label">Estado</InputLabel>
                <Select
                  labelId="pendientes-filtro-estado-label"
                  label="Estado"
                  value={pendientesFiltroEstado}
                  onChange={(e) => setPendientesFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {PEDIDO_ESTADO_PENDIENTES_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Fecha emisión desde"
                type="date"
                value={pendientesFechaDesde}
                onChange={(e) => setPendientesFechaDesde(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
              />
              <TextField
                label="Fecha emisión hasta"
                type="date"
                value={pendientesFechaHasta}
                onChange={(e) => setPendientesFechaHasta(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
              />
            </Stack>
            {pendientesFiltrados.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontSize: '0.8rem' }}>
                {pendientes.length === 0
                  ? 'No hay líneas con cantidad pendiente de facturar para este cliente.'
                  : 'No hay pendientes que coincidan con los filtros aplicados.'}
              </Typography>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {pendientesPagina.map((p) => {
                  const notasTexto = normalizeItemNotas(p.notas);
                  const estadoChip = getPedidoEstadoChip(p.estadoMaster);
                  return (
                    <Card
                      key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack spacing={0.75}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Stack spacing={0.15} sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatComprobanteVentaMasterDisplay(p)}
                              </Typography>
                              {p.descripcionPedido ? (
                                <Typography variant="caption" color="text.secondary">
                                  {p.descripcionPedido}
                                </Typography>
                              ) : null}
                            </Stack>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                              }}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {p.fechaEmisionComprobanteMaster
                              ? new Date(p.fechaEmisionComprobanteMaster).toLocaleDateString('es-AR')
                              : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {p.codigoArticulo || '—'}
                          </Typography>
                          {p.descripcionArticulo && (
                            <Typography variant="caption" color="text.secondary">
                              {p.descripcionArticulo}
                            </Typography>
                          )}
                          {notasTexto ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {notasTexto}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                            <Typography variant="caption" color="text.secondary">
                              Pedida: <strong>{p.cantidadPedida}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Entregada: <strong>{cantidadEntregadaPendiente(p)}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Devuelta: <strong>{cantidadDevueltaPendiente(p)}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                              Pend. facturar: {p.pendienteFacturar}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pend. entregar: {p.pendienteEntregar}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                            <Tooltip title="Ver comprobantes facturados y remitidos">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPendienteHiloInfo({
                                    nroFormularioMaster: p.nroFormularioMaster,
                                    nroItemMaster: p.nroItemMaster,
                                    resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                                  })
                                }
                                aria-label="Información facturado y remitido"
                              >
                                <InfoOutlinedIcon fontSize="small" color="info" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => agregarPendiente(p)}
                              disabled={agregandoTodos}
                              sx={{ fontSize: '0.72rem', py: 0.35, px: 1.5 }}
                            >
                              Agregar
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                <TablePagination
                  component="div"
                  count={pendientesFiltrados.length}
                  page={pendientesPage}
                  onPageChange={handlePendientesPageChange}
                  rowsPerPage={pendientesRowsPerPage}
                  onRowsPerPageChange={handlePendientesRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </Stack>
            ) : isMobile ? (
            <>
              <Stack spacing={1.5}>
                {pendientesPagina.map((p) => {
                  const notasTexto = normalizeItemNotas(p.notas);
                  const estadoChip = getPedidoEstadoChip(p.estadoMaster);
                  return (
                    <Card
                      key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}
                      sx={{ width: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}
                    >
                      <CardContent>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatComprobanteVentaMasterDisplay(p)}
                              </Typography>
                              {p.descripcionPedido ? (
                                <Typography variant="body2" color="text.secondary">
                                  {p.descripcionPedido}
                                </Typography>
                              ) : null}
                            </Stack>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {p.fechaEmisionComprobanteMaster
                              ? new Date(p.fechaEmisionComprobanteMaster).toLocaleDateString('es-AR')
                              : '-'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>{p.codigoArticulo || '—'}</strong> {p.descripcionArticulo || ''}
                          </Typography>
                          {notasTexto ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {notasTexto}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" color="text.secondary">
                              Pedida: <strong>{p.cantidadPedida}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Entregada: <strong>{cantidadEntregadaPendiente(p)}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Devuelta: <strong>{cantidadDevueltaPendiente(p)}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                              Pend. facturar: {p.pendienteFacturar}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pend. entregar: {p.pendienteEntregar}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="Ver comprobantes facturados y remitidos">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPendienteHiloInfo({
                                    nroFormularioMaster: p.nroFormularioMaster,
                                    nroItemMaster: p.nroItemMaster,
                                    resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                                  })
                                }
                                aria-label="Información facturado y remitido"
                              >
                                <InfoOutlinedIcon fontSize="small" color="info" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => agregarPendiente(p)}
                              disabled={agregandoTodos}
                            >
                              Agregar
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
              <TablePagination
                component="div"
                count={pendientesFiltrados.length}
                page={pendientesPage}
                onPageChange={handlePendientesPageChange}
                rowsPerPage={pendientesRowsPerPage}
                onRowsPerPageChange={handlePendientesRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 20, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
            ) : (
            <TableContainer>
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    fontSize: '0.75rem',
                    py: 0.6,
                    px: 0.75,
                    lineHeight: 1.25,
                  },
                  '& .MuiTableCell-head': {
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    lineHeight: 1.15,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '22%' }}>Comprobante</TableCell>
                    <TableCell sx={{ width: '7%' }}>Fecha emisión</TableCell>
                    <TableCell sx={{ width: '8%' }}>Estado</TableCell>
                    <TableCell sx={{ width: '24%' }}>Artículo</TableCell>
                    <TableCell align="right" sx={{ width: '5%' }}>Cant. pedida</TableCell>
                    <TableCell align="right" sx={{ width: '5%' }}>Cant. entregada</TableCell>
                    <TableCell align="right" sx={{ width: '5%' }}>Cant. devuelta</TableCell>
                    <TableCell
                      align="right"
                      sx={(theme) => ({
                        width: '6%',
                        fontWeight: 700,
                        color: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.light + '22',
                      })}
                    >
                      Pend. facturar
                    </TableCell>
                    <TableCell align="right" sx={{ width: '5%' }}>Pend. entregar</TableCell>
                    <TableCell align="center" sx={{ width: '3%', p: 0.25 }} />
                    <TableCell align="center" sx={{ width: '10%' }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientesPagina.map((p) => {
                    const notasTexto = normalizeItemNotas(p.notas);
                    const estadoChip = getPedidoEstadoChip(p.estadoMaster);
                    return (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Stack spacing={0.15} sx={{ py: 0.1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.2,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={formatComprobanteVentaMasterDisplay(p)}
                          >
                            {formatComprobanteVentaMasterDisplay(p)}
                          </Typography>
                          {p.descripcionPedido ? (
                            <Tooltip title={p.descripcionPedido} placement="top">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {p.descripcionPedido}
                              </Typography>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {p.fechaEmisionComprobanteMaster
                          ? new Date(p.fechaEmisionComprobanteMaster).toLocaleDateString('es-AR')
                          : ''}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={estadoChip.label}
                          title={estadoChip.label}
                          size="small"
                          sx={{
                            bgcolor: estadoChip.bgColor,
                            color: estadoChip.textColor,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 20,
                            maxWidth: '100%',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={(theme) => ({
                          backgroundColor: theme.palette.primary.light + '0A',
                          verticalAlign: 'top',
                        })}
                      >
                        <Stack spacing={0.15} sx={{ py: 0.1 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'primary.main', lineHeight: 1.2, display: 'block' }}
                          >
                            {p.codigoArticulo || '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', wordBreak: 'break-word', lineHeight: 1.2, display: 'block' }}
                          >
                            {p.descripcionArticulo || ''}
                          </Typography>
                          {notasTexto ? (
                            <Tooltip title={notasTexto} placement="top">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {notasTexto}
                              </Typography>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{cantidadEntregadaPendiente(p)}</TableCell>
                      <TableCell align="right">{cantidadDevueltaPendiente(p)}</TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          fontWeight: 700,
                          color: theme.palette.primary.dark,
                          backgroundColor: theme.palette.primary.light + '14',
                        })}
                      >
                        {p.pendienteFacturar}
                      </TableCell>
                      <TableCell align="right">{p.pendienteEntregar}</TableCell>
                      <TableCell align="center" sx={{ p: 0.25 }}>
                        <Tooltip title="Ver comprobantes facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          onClick={() => agregarPendiente(p)}
                          disabled={agregandoTodos}
                          sx={{ fontSize: '0.72rem', py: 0.35, px: 1, minWidth: 0 }}
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pendientesFiltrados.length}
                page={pendientesPage}
                onPageChange={handlePendientesPageChange}
                rowsPerPage={pendientesRowsPerPage}
                onRowsPerPageChange={handlePendientesRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 20, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
            )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void agregarTodosPendientes()}
            disabled={pendientesFiltrados.length === 0 || loadingPendientes || agregandoTodos}
            startIcon={agregandoTodos ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {agregandoTodos ? 'Agregando…' : 'Agregar todos'}
          </Button>
          <Button onClick={() => setOpenPendientes(false)} disabled={agregandoTodos}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Facturado y remitido (detalle)</DialogTitle>
        <DialogContent>
          {pendienteHiloInfo?.resumenLinea ? (
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              {pendienteHiloInfo.resumenLinea}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Línea master {pendienteHiloInfo?.nroFormularioMaster}/{pendienteHiloInfo?.nroItemMaster}
          </Typography>

          {loadingPendienteHilo ? (
            <Typography variant="body2" color="text.secondary">Cargando detalle...</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Cantidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendienteHiloItems.map((c) => (
                    <TableRow key={`${c.nroFormulario}-${c.nroItem}-${c.nroTipoFormulario}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {c.fechaEmision ? new Date(c.fechaEmision).toLocaleDateString('es-AR') : ''}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatComprobanteVentaHiloLine(c)}
                      </TableCell>
                      <TableCell align="right">{c.cantidad}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendienteHiloInfo(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Error emisión AFIP (CAE) ── */}
      <Dialog
        open={Boolean(caeErrorDialog)}
        onClose={() => setCaeErrorDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'error.light',
                color: 'error.contrastText',
                flexShrink: 0,
              }}
            >
              <ErrorOutlineIcon />
            </Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              No se pudo autorizar en AFIP
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: 'error.light',
              bgcolor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(211, 47, 47, 0.06)'
                  : 'rgba(211, 47, 47, 0.12)',
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="body2" fontWeight={600} color="error.dark" gutterBottom>
                Detalle del rechazo
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {caeErrorDialog?.mensaje}
              </Typography>
              {caeErrorDialog?.caeObs
                && caeErrorDialog.caeObs.trim() !== String(caeErrorDialog.mensaje ?? '').trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                  {caeErrorDialog.caeObs}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, px: 0.5 }}>
            Corregí los datos del comprobante y volvé a emitir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          {/* Guardar sin CAE — oculto por ahora
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => void handleGuardarSinCaeDesdeDialogo()}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} /> : 'Guardar sin CAE'}
          </Button>
          */}
          <Button
            variant="contained"
            color="primary"
            autoFocus
            onClick={() => setCaeErrorDialog(null)}
            disabled={saving}
          >
            Corregir
          </Button>
        </DialogActions>
      </Dialog>

      <FacturaPOSGrupoCajaDialog
        open={posCobro.openSeleccionGrupo}
        loading={posCobro.loadingGrupo}
        grupos={posCobro.gruposUsuario}
        selectedId={posCobro.grupoSeleccionPendienteId}
        onSelectId={posCobro.setGrupoSeleccionPendienteId}
        onConfirm={posCobro.confirmarGrupoSeleccion}
        onClose={
          posCobro.grupoCaja ? posCobro.cerrarSeleccionGrupo : undefined
        }
      />

      {/* ── Cobro pendiente (factura OK, recibo falló) ── */}
      <Dialog
        open={Boolean(cobroPendienteDialog)}
        onClose={() => setCobroPendienteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Factura emitida — cobro pendiente</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={1}>
            {cobroPendienteDialog?.comprobanteLabel && (
              <Typography variant="body2">
                Factura: <strong>{cobroPendienteDialog.comprobanteLabel}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="error">
              {cobroPendienteDialog?.mensaje}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              La factura ya fue grabada. Podés reintentar el cobro o registrar el recibo en tesorería.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => {
              const nc = cliente?.nroCliente;
              setCobroPendienteDialog(null);
              navigate(nc ? `/tesoreria/recibos/nuevo?nroCliente=${nc}` : '/tesoreria/recibos/nuevo');
            }}
          >
            Ir a tesorería
          </Button>
          <Button onClick={() => setCobroPendienteDialog(null)}>Cerrar</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void handleReintentarCobro()}
          >
            Reintentar cobro
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog resultado ── */}
      <Dialog
        open={Boolean(facturaEmitida) && !cobroPendienteDialog}
        onClose={() => {
          resetOfferedTracking();
          setFacturaEmitida(null);
          setReciboEmitido(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{modoRescate ? 'Factura rescatada' : 'Venta completada'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={1}>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              ✓ Factura{comprobanteEmitidoId ? `: ${comprobanteEmitidoId}` : ' creada'}
            </Typography>
            {facturaEmitida?.caeNro && (
              <>
                <Typography variant="body2" color="text.secondary">
                  ✓ CAE: <strong>{facturaEmitida.caeNro}</strong>
                </Typography>
                {facturaEmitida.caeVencimiento && (
                  <Typography variant="body2" color="text.secondary">
                    Vencimiento CAE: {facturaEmitida.caeVencimiento}
                  </Typography>
                )}
              </>
            )}
            {reciboEmitido && (
              <Typography variant="body2" color="success.main" fontWeight={600}>
                ✓ Recibo{comprobanteReciboId ? `: ${comprobanteReciboId}` : ''}
              </Typography>
            )}
            {(posCobro.cobroHabilitado &&
              (posCobro.cobroRequerido || posCobro.lineasCobro.length > 0) &&
              !reciboEmitido) && (
              <Typography variant="body2" color="warning.main">
                El recibo de cobro no se generó. Revisá tesorería o reintentá desde el aviso anterior.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {modoRescate && afipRescate?.returnTo ? (
            <Button onClick={() => navigate(afipRescate.returnTo!)}>
              Volver al panel ARCA
            </Button>
          ) : (
            <Button onClick={() => navigate('/ventas/facturas')}>
              {posHotkeyButtonLabel('Ver listado facturas', 'post.listado')}
            </Button>
          )}
          {facturaEmitida?.nroFormulario && posTicketPrinter.modoImpresionPos === 'ticketera' ? (
            <>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={posTicketPrinter.printing}
                onClick={() => void posTicketPrinter.imprimirFactura(facturaEmitida, { forzarPdf: true })}
              >
                {posHotkeyButtonLabel('Imprimir PDF', 'post.imprimirPdf')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={posTicketPrinter.printing}
                onClick={() => void posTicketPrinter.imprimirFactura(facturaEmitida)}
              >
                {posHotkeyButtonLabel('Imprimir ticket', 'post.imprimirTicket')}
              </Button>
            </>
          ) : null}
          {facturaEmitida?.nroFormulario && posTicketPrinter.modoImpresionPos === 'pdf' ? (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              disabled={posTicketPrinter.printing}
              onClick={() => void posTicketPrinter.imprimirFactura(facturaEmitida, { forzarPdf: true })}
            >
              {posHotkeyButtonLabel('Imprimir PDF', 'post.imprimirPdf')}
            </Button>
          ) : null}
          {reciboEmitido?.nroFormulario ? (
            <Button
              variant="outlined"
              onClick={() => void handleImprimirReciboPdf(Number(reciboEmitido.nroFormulario))}
            >
              {posHotkeyButtonLabel('Imprimir recibo', 'post.imprimirRecibo')}
            </Button>
          ) : null}
          <Button variant="contained" onClick={handleNuevaFactura}>
            {posHotkeyButtonLabel('Nueva factura', 'post.nuevaFactura')}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfReciboUrl && pdfReciboId != null && (
        <PDFViewer
          pdfUrl={pdfReciboUrl}
          onClose={handleClosePdfRecibo}
          title={comprobanteReciboId ? `Recibo ${comprobanteReciboId}` : `Recibo ${pdfReciboId}`}
          documentId={`${pdfReciboId}`}
          documentType="recibo"
          downloadFileName={pdfReciboFileName ?? undefined}
        />
      )}

      {posTicketPrinter.pdfFactura && (
        <PDFViewer
          pdfUrl={posTicketPrinter.pdfFactura.url}
          onClose={posTicketPrinter.closePdfFactura}
          title={
            comprobanteEmitidoId
              ? `Factura ${comprobanteEmitidoId}`
              : `Factura ${posTicketPrinter.pdfFactura.nroFormulario}`
          }
          documentId={`${posTicketPrinter.pdfFactura.nroFormulario}`}
          documentType="factura"
          downloadFileName={posTicketPrinter.pdfFactura.fileName ?? undefined}
        />
      )}

      <Dialog open={openGenerarAsientoDialog} onClose={() => setOpenGenerarAsientoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            Se eliminarán todas las líneas del libro diario vinculadas a esta factura (incluido si el asiento estaba
            contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual.
            ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerarAsientoDialog(false)} disabled={generandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={() => void ejecutarGenerarAsiento()} disabled={generandoAsiento}>
            {generandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteInfo360Dialog
        open={info360Cliente != null}
        nroCliente={info360Cliente}
        onClose={() => setInfo360Cliente(null)}
      />

      {postEmisionEmailUi}
    </Box>
  );
};

export default NuevaFacturaPOS;
